import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ReactFlow, ReactFlowProvider, Background, Controls, MiniMap, applyNodeChanges } from '@xyflow/react';
import '@xyflow/react/dist/style.css';
import { reactFlowNodeTypes } from './CustomNodes';
import { ArrowLeft, Plus, Save, Play, Trash2, MessageSquare, Zap, PhoneForwarded, Hash, GitBranch, UserPlus, MessageCircle, FileText, StopCircle, FormInput, MessageSquareText, ArrowRight, Settings, Sparkles, Brain, X, TestTube2, Wrench, PanelRightClose, PanelRightOpen, PanelLeftClose, PanelLeftOpen, Hourglass, Clock, Info, ChevronDown, ChevronUp, CreditCard, Wand2 } from 'lucide-react';
import dagre from 'dagre';
import { agentAPI } from '../services/api';
import { useToast } from '../hooks/use-toast';
import { Card } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import OptimizeNodeModal from './OptimizeNodeModal';
import OptimizeTransitionModal from './OptimizeTransitionModal';
import TransitionAnalysisModal from './TransitionAnalysisModal';
import NodeSequenceTester from './NodeSequenceTester';
import AINodeFixer from './AINodeFixer';

const buildFunctionDialogueText = (data) => {
  let text = '';
  if (data.goal) text += `# ACTIVE GOAL\n${data.goal}\n\n`;
  if (data.opening_gambit) text += `# <speak> OPENING GAMBIT\n${data.opening_gambit}\n\n`;
  if (data.rules) text += `# STRICT GUARDRAILS\n${data.rules}\n\n`;

  if (!data.goal && !data.opening_gambit && !data.rules && data.content) {
    return data.content;
  }

  if (data.content) text += `# STRATEGIC TOOLKIT\n${data.content}\n\n`;
  return text.trim();
};

const extractionVariableName = (variable = {}) =>
  variable.name || variable.variable || '';

const extractionVariableIsMandatory = (variable = {}) =>
  variable.mandatory === true || variable.required === true;

const canonicalTransitionTargetId = (transition = {}) => {
  const distinctTargets = [...new Set([
    transition.nextNode,
    transition.target_node_id,
    transition.targetNode,
    transition.target,
    transition.to,
    transition.resolved_target_node_id,
  ]
    .map(target => String(target || '').trim())
    .filter(Boolean))];
  return distinctTargets.length === 1 ? distinctTargets[0] : '';
};

const withCanonicalExtractionVariableName = (variable = {}, name) => {
  const canonical = { ...variable, name };
  delete canonical.variable;
  return canonical;
};

const withCanonicalExtractionMandatory = (variable = {}, mandatory) => {
  const canonical = { ...variable, mandatory };
  delete canonical.required;
  return canonical;
};

const FlowBuilder = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { toast } = useToast();

  const [viewMode, setViewMode] = useState('linear');
  const [rfInstance, setRfInstance] = useState(null);
  const [nodes, setNodes] = useState([
    {
      id: '1',
      type: 'start',
      label: 'Begin',
      data: {
        whoSpeaksFirst: 'user', // 'ai' or 'user'
        aiSpeaksAfterSilence: true,
        silenceTimeout: 2000, // milliseconds
        transitions: []
      }
    },
    {
      id: '2',
      type: 'conversation',
      label: 'Greeting',
      data: {
        mode: 'script', // 'prompt' or 'script'
        content: 'Hello! I am an AI assistant. How can I help you today?',
        depth_gate_enabled: false,
        depth_gate_threshold: 7,
        transitions: [{ id: '1', condition: '', nextNode: '' }]
      }
    }
  ]);

  const [selectedNode, setSelectedNode] = useState(nodes[0]);
  const [loading, setLoading] = useState(true);
  const [agent, setAgent] = useState(null);
  const [showNodePalette, setShowNodePalette] = useState(false);
  const [showOptimizeModal, setShowOptimizeModal] = useState(false);
  const [optimizeContent, setOptimizeContent] = useState('');
  const [showTransitionModal, setShowTransitionModal] = useState(false);
  const [currentTransitionId, setCurrentTransitionId] = useState(null);
  const [transitionContent, setTransitionContent] = useState('');
  const [showAnalysisModal, setShowAnalysisModal] = useState(false);

  // Webhook tester state
  const [webhookTestVariables, setWebhookTestVariables] = useState({});
  const [webhookTestResponse, setWebhookTestResponse] = useState(null);
  const [webhookTestLoading, setWebhookTestLoading] = useState(false);
  const [showWebhookTester, setShowWebhookTester] = useState(false);
  const [customTestVariables, setCustomTestVariables] = useState([]);  // [{name: '', value: ''}]

  // Node sequence tester state
  const [showNodeTester, setShowNodeTester] = useState(false);

  // AI Node Fixer state
  const [showNodeFixer, setShowNodeFixer] = useState(false);

  // Left Panel state
  const [isLeftPanelCollapsed, setIsLeftPanelCollapsed] = useState(false);

  // Right Panel state
  const [isRightPanelCollapsed, setIsRightPanelCollapsed] = useState(false);
  const onLayout = () => {
    const dagreGraph = new dagre.graphlib.Graph();
    dagreGraph.setDefaultEdgeLabel(() => ({}));
    
    const nodeWidth = 280;
    const nodeHeight = 160;
    
    dagreGraph.setGraph({ rankdir: 'LR', ranksep: 80, nodesep: 40 });
    
    // Add nodes to dagre
    nodes.forEach((node) => {
      dagreGraph.setNode(node.id, { width: nodeWidth, height: nodeHeight });
    });
    
    // Add edges to dagre
    nodes.forEach((n) => {
      if (n.data?.transitions?.length > 0) {
        n.data.transitions.forEach((t) => {
          const target = canonicalTransitionTargetId(t);
          if (target) {
            dagreGraph.setEdge(n.id, target);
          }
        });
      } else {
        const nextIndex = nodes.findIndex((node) => node.id === n.id) + 1;
        if (nextIndex > 0 && nextIndex < nodes.length) {
          dagreGraph.setEdge(n.id, nodes[nextIndex].id);
        }
      }
    });
    
    dagre.layout(dagreGraph);
    
    setNodes((nds) => 
      nds.map((node) => {
        const nodeWithPosition = dagreGraph.node(node.id);
        return {
          ...node,
          position: {
            x: nodeWithPosition.x - nodeWidth / 2,
            y: nodeWithPosition.y - nodeHeight / 2,
          },
        };
      })
    );
    
    toast({
      title: "Flow Arranged",
      description: "Nodes have been automatically organized.",
    });
  };

  useEffect(() => {
    fetchAgentFlow();
  }, [id]);

  // Listen for inline editing updates from custom nodes
  useEffect(() => {
    const handleNodeUpdate = (e) => {
      const { nodeId, key, value } = e.detail;
      setNodes((nds) => {
        const newNodes = nds.map(n => {
          if (n.id === nodeId) {
            return { ...n, data: { ...n.data, [key]: value } };
          }
          return n;
        });
        return newNodes;
      });
      
      setSelectedNode(prev => {
        if (prev && prev.id === nodeId) {
          return { ...prev, data: { ...prev.data, [key]: value } };
        }
        return prev;
      });
    };

    document.addEventListener('nodeDataUpdate', handleNodeUpdate);
    return () => document.removeEventListener('nodeDataUpdate', handleNodeUpdate);
  }, []);

  const fetchAgentFlow = async () => {
    try {
      const [agentRes, flowRes] = await Promise.all([
        agentAPI.get(id),
        agentAPI.getFlow(id)
      ]);
      setAgent(agentRes.data);
      const endpointFlow = Array.isArray(flowRes.data?.flow) ? flowRes.data.flow : [];
      const hydratedDetailFlow = Array.isArray(agentRes.data?.call_flow) ? agentRes.data.call_flow : [];
      const loadedFlow = endpointFlow.length > 0 ? endpointFlow : hydratedDetailFlow;
      if (loadedFlow.length > 0) {
        setNodes(loadedFlow);
        setSelectedNode(loadedFlow[0]);
      }
    } catch (error) {
      console.error('Error fetching agent flow:', error);
      toast({
        title: "Error",
        description: "Failed to load agent flow",
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };

  const saveFlow = async () => {
    try {
      await agentAPI.updateFlow(id, nodes);
      toast({
        title: "Success",
        description: "Flow saved successfully"
      });
    } catch (error) {
      console.error('Error saving flow:', error);
      toast({
        title: "Error",
        description: "Failed to save flow",
        variant: "destructive"
      });
    }
  };

  // Webhook tester function
  const testWebhook = async () => {
    if (!selectedNode || selectedNode.type !== 'function') return;

    const webhookUrl = selectedNode.data?.webhook_url;
    if (!webhookUrl) {
      toast({
        title: "Error",
        description: "Please enter a webhook URL first",
        variant: "destructive"
      });
      return;
    }

    setWebhookTestLoading(true);
    setWebhookTestResponse(null);

    try {
      // Get all variables from the node's extract_variables
      const extractVariables = selectedNode.data?.extract_variables || [];

      // Build the request body - handle both JSON Schema and Template formats
      let parsedBody;
      const bodyTemplate = selectedNode.data?.webhook_body || '';

      // Try to parse as JSON first
      let bodyObj = null;
      try {
        if (bodyTemplate.trim()) {
          bodyObj = JSON.parse(bodyTemplate);
        }
      } catch {
        bodyObj = null;
      }

      // Check if it's a JSON Schema format (has "type" and "properties" keys)
      if (bodyObj && typeof bodyObj === 'object' && bodyObj.type === 'object' && bodyObj.properties) {
        // It's a JSON Schema - build request body from test variables
        console.log('📋 Detected JSON schema format - building request from test variables');
        parsedBody = {};

        // Build request body from schema properties and test variables
        for (const propName of Object.keys(bodyObj.properties)) {
          // Check webhookTestVariables first
          if (webhookTestVariables[propName] !== undefined && webhookTestVariables[propName] !== '') {
            parsedBody[propName] = webhookTestVariables[propName];
          }
          // Then check custom test variables
          else {
            const customVar = customTestVariables.find(v => v.name === propName);
            if (customVar && customVar.value) {
              parsedBody[propName] = customVar.value;
            } else {
              // Property not found - use a placeholder or null
              parsedBody[propName] = null;
            }
          }
        }

        // Check if any variables are missing
        const missingVars = Object.entries(parsedBody)
          .filter(([key, value]) => value === null)
          .map(([key]) => key);

        if (missingVars.length > 0) {
          toast({
            title: "Warning",
            description: `Missing test values for: ${missingVars.join(', ')}. Please fill in the test variables above.`,
            variant: "destructive"
          });
          setWebhookTestLoading(false);
          return;
        }
      } else {
        // It's a template format with {{variable}} placeholders - replace them
        let body = bodyTemplate || '{}';

        // Replace {{variable}} with test values from extractVariables
        for (const varDef of extractVariables) {
          const varName = extractionVariableName(varDef);
          const testValue = webhookTestVariables[varName] || '';
          body = body.replace(new RegExp(`\\{\\{${varName}\\}\\}`, 'g'), testValue);
        }

        // Replace custom variables
        for (const customVar of customTestVariables) {
          if (customVar.name) {
            body = body.replace(new RegExp(`\\{\\{${customVar.name}\\}\\}`, 'g'), customVar.value || '');
          }
        }

        // Also replace any other common variables
        body = body.replace(/\{\{call_id\}\}/g, 'test_call_123');
        body = body.replace(/\{\{user_message\}\}/g, webhookTestVariables['user_message'] || 'Test message');

        // Parse the body as JSON if it's valid
        try {
          parsedBody = JSON.parse(body);
        } catch {
          parsedBody = body;
        }
      }

      const method = selectedNode.data?.webhook_method || 'POST';
      const timeout = (selectedNode.data?.webhook_timeout || 10) * 1000;

      const startTime = Date.now();

      // Make the request through our backend proxy to avoid CORS
      const axiosResponse = await agentAPI.testWebhook({
        url: webhookUrl,
        method: method,
        body: parsedBody,
        headers: selectedNode.data?.webhook_headers || {},
        params: selectedNode.data?.webhook_query_params || {},
        timeout: timeout
      });

      const endTime = Date.now();
      const duration = endTime - startTime;

      const result = axiosResponse.data;

      setWebhookTestResponse({
        success: result.success,
        status: result.status_code,
        duration: duration,
        data: result.response,
        error: result.error,
        request: {
          url: webhookUrl,
          method: method,
          body: parsedBody
        }
      });

      toast({
        title: result.success ? "Webhook Test Successful" : "Webhook Test Failed",
        description: result.success
          ? `Response received in ${duration}ms`
          : result.error || "Request failed",
        variant: result.success ? "default" : "destructive"
      });

    } catch (error) {
      console.error('Webhook test error:', error);
      setWebhookTestResponse({
        success: false,
        error: error.message,
        request: {
          url: webhookUrl,
          method: selectedNode.data?.webhook_method || 'POST'
        }
      });
      toast({
        title: "Error",
        description: "Failed to test webhook: " + error.message,
        variant: "destructive"
      });
    } finally {
      setWebhookTestLoading(false);
    }
  };

  const nodeTypes = [
    { id: 'start', name: 'Start (Greeting)', icon: Play, color: '#22c55e', description: 'First message users see' },
    { id: 'conversation', name: 'Conversation', icon: MessageSquare, color: '#3b82f6', description: 'Regular response node' },
    { id: 'function', name: 'Function', icon: Zap, color: '#8b5cf6', description: 'Execute a function' },
    { id: 'call_transfer', name: 'Call Transfer', icon: PhoneForwarded, color: '#10b981', description: 'Transfer to another number' },
    { id: 'collect_input', name: 'Collect Input', icon: FormInput, color: '#f59e0b', description: 'Gather structured data' },
    { id: 'send_sms', name: 'Send SMS', icon: MessageSquareText, color: '#14b8a6', description: 'Send text message' },
    { id: 'logic_split', name: 'Logic Split', icon: GitBranch, color: '#ec4899', description: 'Conditional branching' },
    { id: 'press_digit', name: 'Press Digit', icon: Hash, color: '#f59e0b', description: 'DTMF input' },
    { id: 'agent_transfer', name: 'Agent Transfer', icon: UserPlus, color: '#06b6d4', description: 'Transfer to human' },
    { id: 'extract_variable', name: 'Extract Variable', icon: FileText, color: '#f97316', description: 'Capture data' },
    { id: 'inbound_webhook', name: 'Wait (Inbound Webhook)', icon: Hourglass, color: '#6366f1', description: 'Wait for external trigger' },
    { id: 'time_converter', name: 'Time Converter', icon: Clock, color: '#0ea5e9', description: 'Convert timezone slots silently' },
    { id: 'stripe_payment', name: 'Stripe Payment', icon: CreditCard, color: '#6366f1', description: 'Process direct card payment' },
    { id: 'ending', name: 'Ending', icon: StopCircle, color: '#ef4444', description: 'End conversation' }
  ];

  const addNode = (type) => {
    let nodeData;

    if (type.id === 'start') {
      nodeData = {
        whoSpeaksFirst: 'user',
        aiSpeaksAfterSilence: true,
        silenceTimeout: 2000,
        failed_transition_instructions: 'Stay on current node and fulfill ACTIVE GOAL',
        transitions: []
      };
    } else if (type.id === 'ending') {
      nodeData = {
        script: 'Thank you for calling. Goodbye!',
        auto_end: true,
        transitions: [{ id: Date.now().toString(), condition: '', nextNode: '' }]
      };
    } else if (type.id === 'function') {
      nodeData = {
        webhook_url: '',
        webhook_method: 'POST',
        webhook_headers: {},
        webhook_body: '',  // Start empty to avoid template issues
        webhook_timeout: 10,
        response_variable: 'webhook_response',
        extract_variables: [],  // Initialize empty array for variable extraction
        speak_during_execution: false,  // NEW: Whether to speak before webhook executes
        dialogue_type: 'static',  // NEW: 'static' or 'prompt' (AI-generated)
        dialogue_text: '',  // NEW: The text to speak (static) or prompt for AI
        wait_for_result: true,  // NEW: Wait for webhook to complete before transitioning
        wait_for_user_reply: false, // NEW: Wait for user to reply after webhook completes before evaluating transitions
        block_interruptions: true,  // NEW: Prevent user interruptions during dialogue
        failed_transition_instructions: 'Stay on current node and fulfill ACTIVE GOAL',
        transitions: [{ id: '1', condition: 'After webhook completes', nextNode: '' }]
      };
    } else if (type.id === 'call_transfer' || type.id === 'agent_transfer') {
      nodeData = {
        transfer_type: 'cold',  // cold or warm
        destination_type: type.id === 'call_transfer' ? 'phone' : 'agent',
        destination: '',  // phone number or agent name
        transfer_message: 'Please hold while I transfer your call...',
        timeout_secs: 30,  // How long to ring the destination (5-120s)
        transitions: []  // No transitions after transfer
      };
    } else if (type.id === 'collect_input') {
      nodeData = {
        variable_name: 'user_input',
        input_type: 'text',  // text, email, phone, number
        prompt_message: 'Please provide the information.',
        error_message: 'That does not look right. Please try again.',
        failed_transition_instructions: 'Stay on current node and fulfill ACTIVE GOAL',
        transitions: [{ id: '1', condition: 'After valid input collected', nextNode: '' }]
      };
    } else if (type.id === 'send_sms') {
      nodeData = {
        phone_number: '',  // Can use variable reference
        sms_message: '',
        failed_transition_instructions: 'Stay on current node and fulfill ACTIVE GOAL',
        transitions: [{ id: '1', condition: 'After SMS sent', nextNode: '' }]
      };
    } else if (type.id === 'logic_split') {
      nodeData = {
        conditions: [
          { id: '1', variable: '', operator: 'equals', value: '', nextNode: '' }
        ],
        default_next_node: ''
      };
    } else if (type.id === 'press_digit') {
      nodeData = {
        prompt_message: 'Please press a digit from 0 to 9.',
        digit_mappings: {},
        transitions: []
      };
    } else if (type.id === 'extract_variable') {
      nodeData = {
        variable_name: 'extracted_data',
        extraction_prompt: 'Extract the relevant information',
        failed_transition_instructions: 'Stay on current node and fulfill ACTIVE GOAL',
        transitions: [{ id: '1', condition: 'After extraction', nextNode: '' }]
      };
    } else if (type.id === 'inbound_webhook') {
      nodeData = {
        timeout_duration: 60,
        wait_message: 'Please wait a moment while I process that.',
        prompt_context: 'The user is waiting. Reassure them that we are still working on it but avoid making promises.',
        failed_transition_instructions: 'Stay on current node and fulfill ACTIVE GOAL',
        transitions: [
          { id: '1', condition: 'If webhook succeeds (System Trigger)', nextNode: '' },
          { id: '2', condition: 'If webhook times out', nextNode: '' }
        ]
      };
    } else if (type.id === 'time_converter') {
      nodeData = {
        source_variable: '',
        output_variable: 'convertedAvailability',
        timezone_variable: 'timeZone',
        transitions: [{ id: '1', condition: 'After conversion completes', nextNode: '' }]
      };
    } else if (type.id === 'stripe_payment') {
      nodeData = {
        amount_variable: 'amount',
        currency: 'usd',
        card_number_variable: 'cardNumber',
        exp_month_variable: 'expMonth',
        exp_year_variable: 'expYear',
        cvc_variable: 'cvc',
        wait_for_result: true,
        wait_message: 'Please wait a moment while I process your payment.',
        failed_transition_instructions: 'Stay on current node and fulfill ACTIVE GOAL',
        transitions: [
          { id: '1', condition: 'If payment is successful', nextNode: '' },
          { id: '2', condition: 'If payment is declined', nextNode: '' }
        ]
      };
    } else {
      nodeData = {
        mode: 'script',
        content: '',
        depth_gate_enabled: false,
        depth_gate_threshold: 7,
        failed_transition_instructions: 'Stay on current node and fulfill ACTIVE GOAL',
        transitions: [{ id: '1', condition: '', nextNode: '' }]
      };
    }

    const newNode = {
      id: Date.now().toString(),
      type: type.id,
      label: type.name,
      data: nodeData
    };

    setNodes([...nodes, newNode]);
    setSelectedNode(newNode);
    setShowNodePalette(false);
  };

  const updateNode = (nodeId, updates) => {
    setNodes(nodes.map(n => {
      if (n.id === nodeId) {
        const updatedNode = { ...n, ...updates };
        if (updates.label !== undefined) {
          updatedNode.data = { ...updatedNode.data, label: updates.label };
        }
        return updatedNode;
      }
      return n;
    }));
    
    const newSelected = { ...selectedNode, ...updates };
    if (updates.label !== undefined) {
      newSelected.data = { ...newSelected.data, label: updates.label };
    }
    setSelectedNode(newSelected);
  };

  const addTransition = () => {
    const newTransitions = [
      ...selectedNode.data.transitions,
      { id: Date.now().toString(), condition: '', nextNode: '' }
    ];
    updateNode(selectedNode.id, {
      data: { ...selectedNode.data, transitions: newTransitions }
    });
  };

  const updateTransition = (transitionId, field, value) => {
    const newTransitions = selectedNode.data.transitions.map(t =>
      t.id === transitionId
        ? (
          field === 'nextNode'
            ? {
              ...t,
              nextNode: value,
              target_node_id: value,
              targetNode: value,
              target: value,
              to: value,
              resolved_target_node_id: value,
            }
            : { ...t, [field]: value }
        )
        : t
    );
    updateNode(selectedNode.id, {
      data: { ...selectedNode.data, transitions: newTransitions }
    });
  };

  const updateTransitionFields = (transitionId, fields) => {
    const hydratedFields = Object.prototype.hasOwnProperty.call(fields, 'nextNode')
      ? {
        ...fields,
        target_node_id: fields.nextNode,
        targetNode: fields.nextNode,
        target: fields.nextNode,
        to: fields.nextNode,
        resolved_target_node_id: fields.nextNode,
      }
      : fields;
    const newTransitions = selectedNode.data.transitions.map(t =>
      t.id === transitionId ? { ...t, ...hydratedFields } : t
    );
    updateNode(selectedNode.id, {
      data: { ...selectedNode.data, transitions: newTransitions }
    });
  };

  const deleteTransition = (transitionId) => {
    const newTransitions = selectedNode.data.transitions.filter(t => t.id !== transitionId);
    updateNode(selectedNode.id, {
      data: { ...selectedNode.data, transitions: newTransitions }
    });
  };

  const deleteNode = (nodeId) => {
    if (nodes.length > 1) {
      const newNodes = nodes.filter(n => n.id !== nodeId);
      setNodes(newNodes);
      setSelectedNode(newNodes[0]);
    }
  };

  const handleOptimizeClick = () => {
    setOptimizeContent(selectedNode.data.content || '');
    setShowOptimizeModal(true);
  };

  const handleApplyOptimized = (optimizedContent) => {
    updateNode(selectedNode.id, {
      data: { ...selectedNode.data, content: optimizedContent }
    });
  };

  if (loading) {
    return (
      <div className="p-8 flex items-center justify-center h-screen bg-white">
        <div className="text-gray-900 text-xl">Loading flow...</div>
      </div>
    );
  }

  return (
    <div className="h-screen flex flex-col animated-bg">
      {/* Header */}
      <div className="bg-white/15 backdrop-blur-md border-b border-white/30 p-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button
              onClick={() => navigate('/agents')}
              variant="ghost"
              className="text-gray-500 hover:text-gray-900"
              size="sm"
            >
              <ArrowLeft size={20} />
            </Button>
            <Button
              onClick={() => navigate(`/agents/${id}/edit`)}
              variant="ghost"
              className="text-gray-500 hover:text-gray-900"
              size="sm"
            >
              <Settings size={20} className="mr-2" />
              Settings
            </Button>
            <div>
              <h1 className="text-xl font-bold text-gray-900">Call Flow Editor</h1>
              <p className="text-sm text-gray-500">{agent?.name} - Design your conversation flow</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
          <div className="flex bg-white/10 p-1 rounded-lg backdrop-blur-md border border-white/20 mr-4">
            <button
              onClick={() => setViewMode('linear')}
              className={`px-3 py-1 text-sm rounded-md transition-all ${viewMode === 'linear' ? 'bg-purple-600 text-white shadow' : 'text-gray-400 hover:text-white'}`}
            >
              Linear
            </button>
            <button
              onClick={() => setViewMode('flowchart')}
              className={`px-3 py-1 text-sm rounded-md transition-all ${viewMode === 'flowchart' ? 'bg-purple-600 text-white shadow' : 'text-gray-400 hover:text-white'}`}
            >
              Flowchart
            </button>
          </div>
            {viewMode === 'flowchart' && (
              <Button
                onClick={onLayout}
                variant="outline"
                size="sm"
                className="border-blue-500 text-blue-600 hover:bg-blue-50 hover:text-blue-700"
              >
                <Wand2 size={16} className="mr-2" />
                Auto Arrange
              </Button>
            )}
            <Button
              onClick={() => navigate(`/agents/${id}/test`)}
              variant="outline"
              className="border-green-600 text-green-400 hover:bg-green-600/20 hover:text-green-300"
              size="sm"
            >
              <Play size={16} className="mr-2" />
              Test Agent
            </Button>
            <Button
              onClick={() => setShowNodeTester(true)}
              variant="outline"
              size="sm"
              className="border-purple-500 text-purple-400 hover:bg-purple-500/10"
            >
              <TestTube2 size={16} className="mr-2" />
              Test Nodes
            </Button>
            <Button
              onClick={() => setShowNodeFixer(true)}
              variant="outline"
              size="sm"
              className="bg-gradient-to-r from-[#a855f7] to-[#7c4dff] text-white border-none hover:opacity-90"
            >
              <Wrench size={16} className="mr-2" />
              AI Fix Nodes
            </Button>
            <Button
              onClick={saveFlow}
              className="bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700"
              size="sm"
            >
              <Save size={16} className="mr-2" />
              Save
            </Button>
          </div>
        </div>
      </div>

      <div className="flex-1 flex overflow-hidden">
        {/* Left Sidebar - Node Palette */}
        <div className={`${isLeftPanelCollapsed ? 'w-20' : 'w-64'} bg-[#F8FAFC] border-r border-gray-200 p-4 overflow-y-auto transition-all duration-300 flex flex-col z-10`}>
          <div className="flex items-center justify-between mb-4">
            {!isLeftPanelCollapsed && <h2 className="text-gray-900 font-semibold whitespace-nowrap overflow-hidden">Node Types</h2>}
            <Button
              variant="ghost"
              size="icon"
              onClick={() => setIsLeftPanelCollapsed(!isLeftPanelCollapsed)}
              className="text-gray-500 hover:text-gray-900 shrink-0"
            >
              {isLeftPanelCollapsed ? <PanelLeftOpen size={20} /> : <PanelLeftClose size={20} />}
            </Button>
          </div>
          
          <div className="space-y-2 flex-1 overflow-y-auto overflow-x-hidden">
            {nodeTypes.map((type) => {
              const Icon = type.icon;
              return (
                <button
                  key={type.id}
                  onClick={() => addNode(type)}
                  className={`w-full flex items-center ${isLeftPanelCollapsed ? 'justify-center p-2' : 'gap-3 p-3'} bg-white hover:bg-gray-50 rounded-lg transition-all border border-gray-200 hover:-translate-x-[-4px] shadow-sm`}
                  style={{ borderLeftColor: type.color, borderLeftWidth: '4px' }}
                >
                  <Icon size={18} style={{ color: type.color }} className="shrink-0" title={type.name} />
                  {!isLeftPanelCollapsed && <span className="text-gray-900 text-sm whitespace-nowrap overflow-hidden">{type.name}</span>}
                </button>
              );
            })}
          </div>
        </div>

        
        {/* Center - Flow Canvas */}
        <div className="flex-1 overflow-hidden relative">
          {viewMode === 'linear' ? (
            <div className="p-6 overflow-auto h-full">
              <div className="space-y-4 max-w-3xl mx-auto flex flex-col items-center">
                {nodes.map((node, index) => {
                  const nodeType = nodeTypes.find(t => t.id === node.type);
                  const Icon = nodeType?.icon || MessageSquare;
                  const isSelected = selectedNode?.id === node.id;
                  return (
                    <div key={node.id} id={`node-item-${node.id}`} className="w-full">
                      <Card
                        onClick={() => setSelectedNode(node)}
                        className={`p-4 cursor-pointer glass-glow w-full ${isSelected ? 'ring-2 ring-purple-500 ring-offset-2 ring-offset-transparent' : ''}`}
                        style={{ borderLeftColor: nodeType?.color, borderLeftWidth: '4px', wordBreak: 'break-word', overflowWrap: 'anywhere' }}
                      >
                        <div className="flex items-start justify-between mb-2">
                          <div className="flex items-center gap-3">
                            <div className="p-2 rounded-lg" style={{ backgroundColor: `${nodeType?.color}20` }}>
                              <Icon size={20} style={{ color: nodeType?.color }} />
                            </div>
                            <div>
                              <h3 className="text-gray-900 font-semibold">{node.label}</h3>
                              <p className="text-gray-500 text-xs">{nodeType?.name}</p>
                            </div>
                          </div>
                          <Button
                            onClick={(e) => {
                              e.stopPropagation();
                              deleteNode(node.id);
                            }}
                            variant="ghost"
                            size="sm"
                            className="text-red-400 hover:text-red-300"
                          >
                            <Trash2 size={16} />
                          </Button>
                        </div>
                        {node.data.prompt && (
                          <p className="text-gray-700 text-sm mt-2 line-clamp-2">{node.data.prompt}</p>
                        )}
                        {node.type === 'function' && node.data.speak_during_execution && node.data.dialogue_text && (
                          <div className="mt-3 bg-purple-900/30 border border-purple-600/50 rounded-lg p-3">
                            <div className="flex items-center gap-2 mb-2">
                              <span className="text-xs font-semibold text-purple-400">
                                {node.data.dialogue_type === 'static' ? '💬 Static Sentence' : '🤖 AI Prompt'}
                              </span>
                            </div>
                            <p className="text-gray-700 text-sm italic line-clamp-2">{node.data.dialogue_text}</p>
                          </div>
                        )}
                        {node.data.transitions && node.data.transitions.length > 0 && (
                          <div className="mt-3 space-y-1">
                            {node.data.transitions.map((transition) => (
                              <div key={transition.id} className="text-xs text-gray-500">
                                {transition.condition && `→ ${transition.condition}`}
                              </div>
                            ))}
                          </div>
                        )}
                      </Card>
                      {index < nodes.length - 1 && (
                        <div className="flex flex-col items-center justify-center py-2">
                          <div className="w-[2px] h-10 bg-purple-500 relative">
                            <div className="absolute -bottom-[5px] left-1/2 -translate-x-1/2 border-l-[6px] border-l-transparent border-r-[6px] border-r-transparent border-t-[8px] border-t-purple-500"></div>
                          </div>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          ) : (
            <ReactFlowProvider>
              <div className="h-full w-full reactflow-wrapper">
                <ReactFlow
                  nodes={nodes.map((n, i) => ({
                    ...n,
                    position: n.position || { x: 250, y: i * 250 }
                  }))}
                  edges={nodes.flatMap(n => {
                    const edges = [];
                    if (n.data?.transitions?.length > 0) {
                      n.data.transitions.forEach(t => {
                        const target = canonicalTransitionTargetId(t);
                        if (target) {
                          edges.push({
                            id: `e-${n.id}-${target}-${t.id || Date.now()}`,
                            source: n.id,
                            target,
                            sourceHandle: t.id,
                            type: 'smoothstep',
                            animated: true,
                            style: { stroke: '#a855f7', strokeWidth: 2 }
                          });
                        }
                      });
                    } else {
                      // Fallback: connect to the next node in array if no explicit transitions
                      const nextIndex = nodes.findIndex(node => node.id === n.id) + 1;
                      if (nextIndex > 0 && nextIndex < nodes.length) {
                        edges.push({
                          id: `e-${n.id}-${nodes[nextIndex].id}`,
                          source: n.id,
                          target: nodes[nextIndex].id,
                          sourceHandle: 'default-source',
                          type: 'smoothstep',
                          animated: true,
                          style: { stroke: '#a855f7', strokeWidth: 2 }
                        });
                      }
                    }
                    return edges;
                  })}
                  onNodesChange={(changes) => {
                    setNodes((nds) => {
                      const applied = applyNodeChanges(changes, nds);
                      // Update position when dragged
                      return applied;
                    });
                  }}
                  onNodeClick={(_, node) => {
                    // Update selected node matching original logic
                    const originalNode = nodes.find(n => n.id === node.id);
                    if (originalNode) setSelectedNode(originalNode);
                  }}
                  onInit={setRfInstance}
                  nodeTypes={reactFlowNodeTypes}
                  fitView
                  minZoom={0.01}
                  maxZoom={2}
                >
                  <Background color="#fff" gap={20} variant="dots" />
                  <Controls className="bg-white/10 backdrop-blur-md border border-white/20 fill-white" />
                  <MiniMap nodeColor="#a855f7" maskColor="rgba(0,0,0,0.2)" className="bg-white/10 backdrop-blur-md border border-white/20" />
                </ReactFlow>
              </div>
            </ReactFlowProvider>
          )}
        </div>
{/* Right Sidebar - Node Configuration */}
        {selectedNode && (
          <div className={`${isRightPanelCollapsed ? 'w-16' : 'w-96'} bg-[#F8FAFC] border-l border-gray-200 p-4 overflow-y-auto transition-all duration-300 relative flex flex-col z-10`}>
            <div className="flex items-center justify-between mb-4">
              {!isRightPanelCollapsed && <h2 className="text-gray-900 font-semibold">Node Settings</h2>}
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setIsRightPanelCollapsed(!isRightPanelCollapsed)}
                className={`text-gray-500 hover:text-gray-900 ${isRightPanelCollapsed ? 'w-full px-0' : ''}`}
              >
                {isRightPanelCollapsed ? <PanelRightOpen size={20} /> : <PanelRightClose size={20} />}
              </Button>
            </div>

            {!isRightPanelCollapsed && (
              <div className="space-y-4">
                <div>
                  <Label className="text-gray-700">Node Label</Label>
                  <Input
                  value={selectedNode.label}
                  onChange={(e) => updateNode(selectedNode.id, { label: e.target.value })}
                  className="bg-white border-gray-200 text-gray-900 mt-2"
                />
              </div>

              {/* 🛑 DON'T INTERRUPT - TOP OF SETTINGS (GLOBAL FOR SPEAKING NODES) */}
              {(selectedNode.type === 'conversation' || selectedNode.type === 'function' || selectedNode.type === 'start' || selectedNode.type === 'collect_input' || selectedNode.type === 'extract_variable') && (
                <div className="border border-gray-200 rounded-lg p-4 bg-white shadow-sm mt-4 mb-2">
                  <div className="flex items-center justify-between">
                    <div>
                      <div className="flex items-center gap-2 mb-1">
                        <span className="text-2xl">🛑</span>
                        <Label className="text-gray-900 font-semibold text-base">Don't Interrupt</Label>
                      </div>
                      <p className="text-sm text-gray-500">
                        Block user from interrupting while AI speaks on this node
                      </p>
                    </div>
                    <label className="inline-flex items-center cursor-pointer">
                      <input
                        type="checkbox"
                        checked={selectedNode.data?.block_interruptions === true}
                        onChange={(e) => updateNode(selectedNode.id, {
                          data: { ...selectedNode.data, block_interruptions: e.target.checked }
                        })}
                        className="sr-only peer"
                      />
                      <div className="relative w-14 h-7 bg-gray-700 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-red-800 rounded-full peer peer-checked:after:translate-x-full rtl:peer-checked:after:-translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:start-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-6 after:w-6 after:transition-all peer-checked:bg-red-600"></div>
                    </label>
                  </div>
                </div>
              )}

              {/* 🛡️ STT CONTEXT GUARD (AUTO-CORRECT PHONETIC ERRORS) */}
              {(selectedNode.type === 'conversation' || selectedNode.type === 'function' || selectedNode.type === 'start' || selectedNode.type === 'collect_input' || selectedNode.type === 'extract_variable') && (
                <div className="border border-gray-200 rounded-lg p-4 bg-white shadow-sm shadow-lg mt-4 mb-2">
                  <div className="flex items-center justify-between">
                    <div>
                      <div className="flex items-center gap-2 mb-1">
                        <span className="text-xl">🛡️</span>
                        <Label className="text-gray-900 font-semibold text-base">STT Phonetic Auto-Correct</Label>
                      </div>
                      <p className="text-sm text-gray-500">
                        Slower latency (~600ms), but auto-corrects speech-to-text errors based on the node context.
                      </p>
                    </div>
                    <label className="inline-flex items-center cursor-pointer">
                      <input
                        type="checkbox"
                        checked={selectedNode.data?.enable_stt_context_guard === true}
                        onChange={(e) => updateNode(selectedNode.id, {
                          data: { ...selectedNode.data, enable_stt_context_guard: e.target.checked }
                        })}
                        className="sr-only peer"
                      />
                      <div className="relative w-11 h-6 bg-gray-700 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-800 rounded-full peer peer-checked:after:translate-x-full rtl:peer-checked:after:-translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:start-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
                    </label>
                  </div>
                </div>
              )}

              {/* Start node settings */}
              {selectedNode.type === 'start' && (
                <div className="space-y-4 border-t border-gray-200 pt-4">
                  <h3 className="text-gray-900 font-semibold">Begin Settings</h3>

                  <div>
                    <Label className="text-gray-700">Who speaks first</Label>
                    <div className="space-y-2 mt-2">
                      <div
                        onClick={() => updateNode(selectedNode.id, {
                          data: { ...selectedNode.data, whoSpeaksFirst: 'ai' }
                        })}
                        className={`p-3 rounded-lg border-2 cursor-pointer transition-all ${selectedNode.data.whoSpeaksFirst === 'ai'
                          ? 'border-blue-500 bg-blue-500/10'
                          : 'border-gray-200 hover:border-gray-600'
                          }`}
                      >
                        <div className="flex items-center">
                          <div className={`w-5 h-5 rounded-full border-2 mr-3 flex items-center justify-center ${selectedNode.data.whoSpeaksFirst === 'ai' ? 'border-blue-500' : 'border-gray-600'
                            }`}>
                            {selectedNode.data.whoSpeaksFirst === 'ai' && (
                              <div className="w-3 h-3 rounded-full bg-blue-500"></div>
                            )}
                          </div>
                          <span className="text-gray-900">AI speaks first</span>
                        </div>
                      </div>

                      <div
                        onClick={() => updateNode(selectedNode.id, {
                          data: { ...selectedNode.data, whoSpeaksFirst: 'user' }
                        })}
                        className={`p-3 rounded-lg border-2 cursor-pointer transition-all ${selectedNode.data.whoSpeaksFirst === 'user'
                          ? 'border-blue-500 bg-blue-500/10'
                          : 'border-gray-200 hover:border-gray-600'
                          }`}
                      >
                        <div className="flex items-center">
                          <div className={`w-5 h-5 rounded-full border-2 mr-3 flex items-center justify-center ${selectedNode.data.whoSpeaksFirst === 'user' ? 'border-blue-500' : 'border-gray-600'
                            }`}>
                            {selectedNode.data.whoSpeaksFirst === 'user' && (
                              <div className="w-3 h-3 rounded-full bg-blue-500"></div>
                            )}
                          </div>
                          <span className="text-gray-900">User speaks first</span>
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="flex items-center justify-between p-3 bg-white rounded-lg">
                    <div>
                      <Label className="text-gray-700">AI starts speaking after silence</Label>
                      <p className="text-xs text-gray-500 mt-1">
                        When enabled, the AI will automatically speak if the user doesn't start the conversation within the timeout duration.
                      </p>
                    </div>
                    <button
                      onClick={() => updateNode(selectedNode.id, {
                        data: { ...selectedNode.data, aiSpeaksAfterSilence: !selectedNode.data.aiSpeaksAfterSilence }
                      })}
                      className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${selectedNode.data.aiSpeaksAfterSilence ? 'bg-blue-600' : 'bg-gray-700'
                        }`}
                    >
                      <span
                        className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${selectedNode.data.aiSpeaksAfterSilence ? 'translate-x-6' : 'translate-x-1'
                          }`}
                      />
                    </button>
                  </div>

                  {selectedNode.data.aiSpeaksAfterSilence && (
                    <div>
                      <Label className="text-gray-700">Silence Time</Label>
                      <p className="text-xs text-gray-500 mb-2">
                        Time to wait before the assistant starts speaking if the user stays silent.
                      </p>
                      <div className="flex items-center gap-4">
                        <input
                          type="range"
                          min="500"
                          max="10000"
                          step="500"
                          value={selectedNode.data.silenceTimeout}
                          onChange={(e) => updateNode(selectedNode.id, {
                            data: { ...selectedNode.data, silenceTimeout: parseInt(e.target.value) }
                          })}
                          className="custom-slider"
                        />
                        <span className="text-gray-900 font-semibold min-w-[3rem]">
                          {(selectedNode.data.silenceTimeout / 1000).toFixed(1)}s
                        </span>
                      </div>
                    </div>
                  )}

                  <div className="mt-6 pt-4 border-t border-gray-200">
                    <Label className="text-gray-700">Initial Node</Label>
                    <p className="text-xs text-gray-500 mb-3 mt-1">
                      Choose which node the AI should evaluate first when the call begins.
                    </p>
                    <Select
                      value={selectedNode.data.first_node_id || 'none'}
                      onValueChange={(value) => updateNode(selectedNode.id, {
                        data: { ...selectedNode.data, first_node_id: value === 'none' ? null : value }
                      })}
                    >
                      <SelectTrigger className="bg-white border-gray-200 text-gray-900 w-full">
                        <SelectValue placeholder="Default (Oldest Interactive Node)" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="none">Default (Oldest Interactive Node)</SelectItem>
                        {nodes
                          .filter(n => ['conversation', 'collect_input', 'press_digit', 'extract_variable', 'function', 'logic_split'].includes(n.type))
                          .map((n) => (
                            <SelectItem key={n.id} value={n.id}>
                              {n.label} ({nodeTypes.find(t => t.id === n.type)?.name})
                            </SelectItem>
                          ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              )}

              {/* Function/Webhook node settings */}
              {selectedNode.type === 'function' && (
                <div className="space-y-4 border-t border-gray-200 pt-4">
                  <h3 className="text-gray-900 font-semibold">Webhook/Function Settings</h3>

                  {/* Function Behavior Controls */}
                  <div className="bg-white border border-gray-200 rounded-lg p-4 shadow-sm space-y-4">
                    <h4 className="text-gray-900 font-medium">⚙️ Function Behavior</h4>

                    {/* Speak During Execution */}
                    <div>
                      <div className="flex items-center justify-between mb-2">
                        <Label className="text-gray-700">Speak During Execution</Label>
                        <label className="inline-flex items-center cursor-pointer">
                          <input
                            type="checkbox"
                            checked={selectedNode.data?.speak_during_execution === true}
                            onChange={(e) => updateNode(selectedNode.id, {
                              data: { ...selectedNode.data, speak_during_execution: e.target.checked }
                            })}
                            className="sr-only peer"
                          />
                          <div className="relative w-11 h-6 bg-gray-700 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-purple-800 rounded-full peer peer-checked:after:translate-x-full rtl:peer-checked:after:-translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:start-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-purple-600"></div>
                        </label>
                      </div>
                      <p className="text-xs text-gray-500">Agent will say something before executing the webhook (e.g., "One moment...")</p>
                    </div>

                    {/* Dialogue Configuration - Only show if speak_during_execution is enabled */}
                    {selectedNode.data?.speak_during_execution && (
                      <div className="pl-4 border-l-2 border-purple-600 space-y-3">
                        <div>
                          <Label className="text-gray-700">Dialogue Type</Label>
                          <Select
                            value={selectedNode.data?.dialogue_type || 'static'}
                            onValueChange={(value) => updateNode(selectedNode.id, {
                              data: { ...selectedNode.data, dialogue_type: value }
                            })}
                          >
                            <SelectTrigger className="bg-white border-gray-200 text-gray-900 mt-2">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="static">Static Sentence</SelectItem>
                              <SelectItem value="prompt">AI Generated (Prompt)</SelectItem>
                            </SelectContent>
                          </Select>
                          <p className="text-xs text-gray-500 mt-1">
                            {selectedNode.data?.dialogue_type === 'static'
                              ? 'Use exact text you specify'
                              : 'AI will generate response based on your prompt'}
                          </p>
                        </div>

                        {selectedNode.data?.dialogue_type === 'static' ? (
                          <div>
                            <Label className="text-gray-700">Static Text</Label>
                            <Textarea
                              value={selectedNode.data?.dialogue_text || ''}
                              onChange={(e) => updateNode(selectedNode.id, {
                                data: { ...selectedNode.data, dialogue_text: e.target.value }
                              })}
                              className="bg-white border-gray-200 text-gray-900 mt-2"
                              rows={3}
                              placeholder="One moment, let me check that for you..."
                            />
                          </div>
                        ) : (
                          <div className="space-y-4">
                            {/* 1. Active Goal */}
                            <div className="bg-white border border-gray-200 rounded-lg p-3 shadow-sm">
                              <h4 className="text-gray-900 font-medium mb-1 text-sm">🎯 Active Goal</h4>
                              <Textarea
                                value={selectedNode.data?.goal || ''}
                                onChange={(e) => {
                                  const newData = { ...selectedNode.data, goal: e.target.value };
                                  if (newData.dialogue_type === 'prompt') newData.dialogue_text = buildFunctionDialogueText(newData);
                                  updateNode(selectedNode.id, { data: newData });
                                }}
                                className="bg-white border-gray-300 text-gray-900 text-sm"
                                rows={2}
                                placeholder="e.g., 'Execute the specific function checking availability...'"
                              />
                            </div>

                            {/* 2. Opening Gambit */}
                            <div className="bg-white border border-gray-200 rounded-lg p-3 shadow-sm">
                              <h4 className="text-gray-900 font-medium mb-1 text-sm">🗣️ Opening Gambit</h4>
                              <Textarea
                                value={selectedNode.data?.opening_gambit || ''}
                                onChange={(e) => {
                                  const newData = { ...selectedNode.data, opening_gambit: e.target.value };
                                  if (newData.dialogue_type === 'prompt') newData.dialogue_text = buildFunctionDialogueText(newData);
                                  updateNode(selectedNode.id, { data: newData });
                                }}
                                className="bg-white border-gray-300 text-gray-900 text-sm"
                                rows={2}
                                placeholder="e.g., 'Give me just a second to pull that up...'"
                              />
                            </div>

                            {/* 3. Strict Guardrails */}
                            <div className="bg-white border border-gray-200 rounded-lg p-3 shadow-sm">
                              <h4 className="text-gray-900 font-medium mb-1 text-sm">🛡️ Strict Guardrails</h4>
                              <Textarea
                                value={selectedNode.data?.rules || ''}
                                onChange={(e) => {
                                  const newData = { ...selectedNode.data, rules: e.target.value };
                                  if (newData.dialogue_type === 'prompt') newData.dialogue_text = buildFunctionDialogueText(newData);
                                  updateNode(selectedNode.id, { data: newData });
                                }}
                                className="bg-white border-gray-300 text-gray-900 text-sm"
                                rows={2}
                                placeholder="e.g., '- Do not ask unnecessary questions.'"
                              />
                            </div>

                            {/* 4. Strategic Toolkit */}
                            <div className="bg-white border border-gray-200 rounded-lg p-3 shadow-sm">
                              <h4 className="text-gray-900 font-medium mb-1 text-sm">⚔️ Strategic Toolkit</h4>
                              <Textarea
                                value={selectedNode.data?.content || (!selectedNode.data?.goal && !selectedNode.data?.opening_gambit && !selectedNode.data?.rules ? selectedNode.data?.dialogue_text : '') || ''}
                                onChange={(e) => {
                                  const newData = { ...selectedNode.data, content: e.target.value };
                                  if (newData.dialogue_type === 'prompt') newData.dialogue_text = buildFunctionDialogueText(newData);
                                  updateNode(selectedNode.id, { data: newData });
                                }}
                                className="bg-white border-gray-300 text-gray-900 text-sm"
                                rows={4}
                                placeholder="Handlers for objections/questions and core logical instructions."
                              />
                            </div>
                          </div>
                        )}
                      </div>
                    )}

                    {/* Wait for Result */}
                    <div>
                      <div className="flex items-center justify-between mb-2">
                        <Label className="text-gray-700">Wait for Result</Label>
                        <label className="inline-flex items-center cursor-pointer">
                          <input
                            type="checkbox"
                            checked={selectedNode.data?.wait_for_result !== false}
                            onChange={(e) => updateNode(selectedNode.id, {
                              data: { ...selectedNode.data, wait_for_result: e.target.checked }
                            })}
                            className="sr-only peer"
                          />
                          <div className="relative w-11 h-6 bg-gray-700 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-purple-800 rounded-full peer peer-checked:after:translate-x-full rtl:peer-checked:after:-translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:start-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-purple-600"></div>
                        </label>
                      </div>
                      <p className="text-xs text-gray-500">Wait for the webhook to finish before transitioning to the next node</p>
                    </div>

                    {/* Wait for User Reply */}
                    <div>
                      <div className="flex items-center justify-between mb-2">
                        <Label className="text-gray-700">Wait for User Reply</Label>
                        <label className="inline-flex items-center cursor-pointer">
                          <input
                            type="checkbox"
                            checked={selectedNode.data?.wait_for_user_reply === true}
                            onChange={(e) => updateNode(selectedNode.id, {
                              data: { ...selectedNode.data, wait_for_user_reply: e.target.checked }
                            })}
                            className="sr-only peer"
                          />
                          <div className="relative w-11 h-6 bg-gray-700 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-purple-800 rounded-full peer peer-checked:after:translate-x-full rtl:peer-checked:after:-translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:start-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-purple-600"></div>
                        </label>
                      </div>
                      <p className="text-xs text-gray-500">Pause after execution to let the user answer before evaluating transitions</p>
                    </div>

                    {/* Block Interruptions */}
                    <div>
                      <div className="flex items-center justify-between mb-2">
                        <Label className="text-gray-700">Don't Interrupt (Block Interruptions)</Label>
                        <label className="inline-flex items-center cursor-pointer">
                          <input
                            type="checkbox"
                            checked={selectedNode.data?.block_interruptions !== false}
                            onChange={(e) => updateNode(selectedNode.id, {
                              data: { ...selectedNode.data, block_interruptions: e.target.checked }
                            })}
                            className="sr-only peer"
                          />
                          <div className="relative w-11 h-6 bg-gray-700 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-purple-800 rounded-full peer peer-checked:after:translate-x-full rtl:peer-checked:after:-translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:start-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-purple-600"></div>
                        </label>
                      </div>
                      <p className="text-xs text-gray-500">Users cannot interrupt while AI is speaking</p>
                    </div>
                  </div>

                  {/* Goal - What to achieve if re-prompt needed */}
                  <div className="bg-white border border-gray-200 rounded-lg p-4 shadow-sm">
                    <h4 className="text-gray-900 font-medium mb-2">🎯 Node Goal (Optional)</h4>
                    <p className="text-xs text-gray-500 mb-3">
                      If required variables are missing, AI uses this goal to guide conversation toward collecting them
                    </p>
                    <Textarea
                      value={selectedNode.data?.goal || ''}
                      onChange={(e) => updateNode(selectedNode.id, {
                        data: { ...selectedNode.data, goal: e.target.value }
                      })}
                      className="bg-white border-gray-200 text-gray-900"
                      rows={3}
                      placeholder="e.g., 'Collect the appointment date, time, and customer contact information before booking'"
                    />
                    <p className="text-xs text-gray-500 mt-1">
                      Leave empty to use auto-generated re-prompts for missing variables
                    </p>
                  </div>

                  {/* Variable Extraction Section */}
                  <div className="bg-white border border-gray-200 rounded-lg p-4 shadow-sm">
                    <h4 className="text-gray-900 font-medium mb-2">📊 Variables to Extract</h4>
                    <p className="text-xs text-gray-500 mb-3">
                      AI will extract these variables from previous conversation to send to webhook
                    </p>

                    {(selectedNode.data?.extract_variables || []).map((variable, index) => (
                      <div key={index} className="mb-4 p-4 bg-gray-50/50 rounded border border-gray-200">
                        <div className="flex items-start gap-2 mb-3">
                          <input
                            type="text"
                            value={extractionVariableName(variable)}
                            onChange={(e) => {
                              const newVars = [...(selectedNode.data?.extract_variables || [])];
                              newVars[index] = withCanonicalExtractionVariableName(
                                newVars[index],
                                e.target.value
                              );
                              updateNode(selectedNode.id, {
                                data: { ...selectedNode.data, extract_variables: newVars }
                              });
                            }}
                            className="bg-white border border-gray-200 text-gray-900 flex-1 px-3 py-2 rounded font-semibold"
                            placeholder="Variable name (e.g., appointment_date)"
                          />
                          <button
                            onClick={() => {
                              const newVars = (selectedNode.data?.extract_variables || []).filter((_, i) => i !== index);
                              updateNode(selectedNode.id, {
                                data: { ...selectedNode.data, extract_variables: newVars }
                              });
                            }}
                            className="px-3 py-2 bg-red-600 hover:bg-red-700 text-gray-900 rounded"
                          >
                            ✕
                          </button>
                        </div>

                        {/* Description */}
                        <div className="mb-3">
                          <label className="text-xs text-gray-500 mb-1 block">Description</label>
                          <textarea
                            value={variable.description || ''}
                            onChange={(e) => {
                              const newVars = [...(selectedNode.data?.extract_variables || [])];
                              newVars[index] = { ...newVars[index], description: e.target.value };
                              updateNode(selectedNode.id, {
                                data: { ...selectedNode.data, extract_variables: newVars }
                              });
                            }}
                            className="bg-white border border-gray-200 text-gray-900 text-xs w-full px-3 py-2 rounded"
                            rows={2}
                            placeholder="What to extract (e.g., 'The date mentioned by user for appointment')"
                          />
                        </div>

                        {/* Extraction Hint */}
                        <div className="mb-3">
                          <label className="text-xs text-gray-500 mb-1 block">Extraction Hint (Optional)</label>
                          <input
                            type="text"
                            value={variable.extraction_hint || ''}
                            onChange={(e) => {
                              const newVars = [...(selectedNode.data?.extract_variables || [])];
                              newVars[index] = { ...newVars[index], extraction_hint: e.target.value };
                              updateNode(selectedNode.id, {
                                data: { ...selectedNode.data, extract_variables: newVars }
                              });
                            }}
                            className="bg-white border border-gray-200 text-gray-900 text-xs w-full px-3 py-2 rounded"
                            placeholder="e.g., 'Look for phrases like tomorrow, next week, specific dates'"
                          />
                          <p className="text-xs text-gray-500 mt-1">Helps AI know what patterns to look for</p>
                        </div>

                        {/* Calculation Feature */}
                        <div className="mb-3">
                          <label className="text-xs text-gray-500 mb-1 block">Calculation Feature (Optional)</label>
                          <Select
                            value={variable.calculation_type || 'none'}
                            onValueChange={(value) => {
                              const newVars = [...(selectedNode.data?.extract_variables || [])];
                              if (value === 'none') {
                                delete newVars[index].calculation_type;
                                delete newVars[index].round_to_500;
                              } else {
                                newVars[index] = { ...newVars[index], calculation_type: value };
                              }
                              updateNode(selectedNode.id, {
                                data: { ...selectedNode.data, extract_variables: newVars }
                              });
                            }}
                          >
                            <SelectTrigger className="bg-white border-gray-200 text-purple-200 mt-1 mb-2 text-xs py-1 h-8">
                              <SelectValue placeholder="Standard LLM Extraction" />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="none">Standard LLM Extraction</SelectItem>
                              <SelectItem value="income_normalization">Income Normalization (Backend Math)</SelectItem>
                              <SelectItem value="date_to_ms">Convert Date to Unix Timestamp (ms)</SelectItem>
                              <SelectItem value="current_time_plus_days">Current Time + X Days (ms)</SelectItem>
                            </SelectContent>
                          </Select>
                          <p className="text-xs text-gray-500 mt-1">
                            Bypasses the LLM and calculates using fixed rules in the backend.
                          </p>
                        </div>

                        {variable.calculation_type && variable.calculation_type !== 'none' && (
                          <div className="mb-3 flex items-center gap-2">
                            <input
                              type="checkbox"
                              checked={variable.round_to_500 === true}
                              onChange={(e) => {
                                const newVars = [...(selectedNode.data?.extract_variables || [])];
                                newVars[index] = { ...newVars[index], round_to_500: e.target.checked };
                                updateNode(selectedNode.id, {
                                  data: { ...selectedNode.data, extract_variables: newVars }
                                });
                              }}
                              className="w-4 h-4 text-purple-600 bg-white border-gray-200 rounded"
                            />
                            <label className="text-xs text-gray-700">
                              <span className="font-semibold text-purple-400">Round to Nearest 500</span> - e.g., 6782 becomes 7000
                            </label>
                          </div>
                        )}

                        {/* Allow Update Checkbox */}
                        <div className="mb-3 flex items-center gap-2">
                          <input
                            type="checkbox"
                            checked={variable.allow_update === true}
                            onChange={(e) => {
                              const newVars = [...(selectedNode.data?.extract_variables || [])];
                              newVars[index] = { ...newVars[index], allow_update: e.target.checked };
                              updateNode(selectedNode.id, {
                                data: { ...selectedNode.data, extract_variables: newVars }
                              });
                            }}
                            className="w-4 h-4 text-blue-600 bg-white border-gray-200 rounded"
                          />
                          <label className="text-xs text-gray-700">
                            <span className="font-semibold text-blue-400">Allow Update</span> - Re-extract this variable each time node is visited
                          </label>
                        </div>

                        {/* Required Checkbox */}
                        <div className="mb-3 flex items-center gap-2">
                          <input
                            type="checkbox"
                            checked={extractionVariableIsMandatory(variable)}
                            onChange={(e) => {
                              const newVars = [...(selectedNode.data?.extract_variables || [])];
                              newVars[index] = withCanonicalExtractionMandatory(
                                newVars[index],
                                e.target.checked
                              );
                              updateNode(selectedNode.id, {
                                data: { ...selectedNode.data, extract_variables: newVars }
                              });
                            }}
                            className="w-4 h-4 text-blue-600 bg-white border-gray-200 rounded"
                          />
                          <label className="text-xs text-gray-700">
                            <span className="font-semibold text-red-400">Required</span> - Webhook won't execute until this is provided
                          </label>
                        </div>

                        {/* Re-prompt Text (only show if required) */}
                        {extractionVariableIsMandatory(variable) && (
                          <div>
                            <label className="text-xs text-gray-500 mb-1 block">Re-prompt Type</label>
                            <Select
                              value={variable.reprompt_type || 'static'}
                              onValueChange={(value) => {
                                const newVars = [...(selectedNode.data?.extract_variables || [])];
                                newVars[index] = { ...newVars[index], reprompt_type: value };
                                updateNode(selectedNode.id, {
                                  data: { ...selectedNode.data, extract_variables: newVars }
                                });
                              }}
                            >
                              <SelectTrigger className="bg-white border-gray-200 text-gray-900 mt-1 mb-2">
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="static">Static Sentence (Exact Text)</SelectItem>
                                <SelectItem value="prompt">AI Generated (Prompt)</SelectItem>
                              </SelectContent>
                            </Select>

                            <label className="text-xs text-gray-500 mb-1 block">
                              {variable.reprompt_type === 'prompt' ? 'Re-prompt Instruction' : 'Re-prompt Text'} (if missing)
                            </label>
                            <textarea
                              value={variable.reprompt_text || ''}
                              onChange={(e) => {
                                const newVars = [...(selectedNode.data?.extract_variables || [])];
                                newVars[index] = { ...newVars[index], reprompt_text: e.target.value };
                                updateNode(selectedNode.id, {
                                  data: { ...selectedNode.data, extract_variables: newVars }
                                });
                              }}
                              className="bg-white border border-red-700/50 text-gray-900 text-xs w-full px-3 py-2 rounded"
                              rows={2}
                              placeholder={variable.reprompt_type === 'prompt'
                                ? "Tell the user you need the appointment date and ask when they'd like to schedule"
                                : "I need to know the appointment date. When would you like to schedule?"}
                            />
                            <p className="text-xs text-gray-500 mt-1">
                              {variable.reprompt_type === 'prompt'
                                ? 'AI will generate a natural response based on this instruction'
                                : 'Agent will say these exact words if variable is missing'}
                            </p>
                          </div>
                        )}
                      </div>
                    ))}

                    <button
                      onClick={() => {
                        const newVars = [...(selectedNode.data?.extract_variables || []), { name: '', description: '' }];
                        updateNode(selectedNode.id, {
                          data: { ...selectedNode.data, extract_variables: newVars }
                        });
                      }}
                      className="w-full px-4 py-2 bg-blue-600 hover:bg-blue-700 text-gray-900 rounded text-sm"
                    >
                      + Add Variable
                    </button>
                  </div>

                  <div>
                    <Label className="text-gray-700">Webhook URL</Label>
                    <Input
                      value={selectedNode.data?.webhook_url || ''}
                      onChange={(e) => updateNode(selectedNode.id, {
                        data: { ...selectedNode.data, webhook_url: e.target.value }
                      })}
                      className="bg-white border-gray-200 text-gray-900 mt-2"
                      placeholder="https://your-api.com/webhook"
                    />
                  </div>

                  <div>
                    <Label className="text-gray-700">HTTP Method</Label>
                    <Select
                      value={selectedNode.data?.webhook_method || 'POST'}
                      onValueChange={(value) => updateNode(selectedNode.id, {
                        data: { ...selectedNode.data, webhook_method: value }
                      })}
                    >
                      <SelectTrigger className="bg-white border-gray-200 text-gray-900 mt-2">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="GET">GET</SelectItem>
                        <SelectItem value="POST">POST</SelectItem>
                        <SelectItem value="PUT">PUT</SelectItem>
                        <SelectItem value="PATCH">PATCH</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="mt-4">
                    <Label className="text-gray-700 flex justify-between items-center">
                      Headers
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => {
                          const headers = Array.isArray(selectedNode.data?.webhook_headers) 
                            ? selectedNode.data.webhook_headers 
                            : Object.entries(selectedNode.data?.webhook_headers || {}).map(([k, v]) => ({key: k, value: v}));
                          updateNode(selectedNode.id, {
                            data: { ...selectedNode.data, webhook_headers: [...headers, { key: '', value: '' }] }
                          });
                        }}
                        className="text-blue-400 hover:text-blue-300 h-6 px-2"
                      >
                        <Plus size={14} className="mr-1" /> Add Header
                      </Button>
                    </Label>
                    <div className="space-y-2 mt-2">
                       {(Array.isArray(selectedNode.data?.webhook_headers) 
                          ? selectedNode.data.webhook_headers 
                          : Object.entries(selectedNode.data?.webhook_headers || {}).map(([k, v]) => ({key: k, value: v}))
                       ).length === 0 ? (
                         <p className="text-xs text-gray-500 italic">No custom headers configured</p>
                       ) : (
                         (Array.isArray(selectedNode.data?.webhook_headers) 
                            ? selectedNode.data.webhook_headers 
                            : Object.entries(selectedNode.data?.webhook_headers || {}).map(([k, v]) => ({key: k, value: v}))
                         ).map((header, idx) => (
                           <div key={idx} className="flex items-center gap-2">
                             <Input
                               placeholder="Key (e.g. Authorization)"
                               value={header.key}
                               onChange={(e) => {
                                 const headers = Array.isArray(selectedNode.data?.webhook_headers) 
                                   ? selectedNode.data.webhook_headers 
                                   : Object.entries(selectedNode.data?.webhook_headers || {}).map(([k, v]) => ({key: k, value: v}));
                                 const newHeaders = [...headers];
                                 newHeaders[idx].key = e.target.value;
                                 updateNode(selectedNode.id, { data: { ...selectedNode.data, webhook_headers: newHeaders } });
                               }}
                               className="bg-white border-gray-200 text-gray-900"
                             />
                             <Input
                               placeholder="Value (e.g. Bearer token)"
                               value={header.value}
                               onChange={(e) => {
                                 const headers = Array.isArray(selectedNode.data?.webhook_headers) 
                                   ? selectedNode.data.webhook_headers 
                                   : Object.entries(selectedNode.data?.webhook_headers || {}).map(([k, v]) => ({key: k, value: v}));
                                 const newHeaders = [...headers];
                                 newHeaders[idx].value = e.target.value;
                                 updateNode(selectedNode.id, { data: { ...selectedNode.data, webhook_headers: newHeaders } });
                               }}
                               className="bg-white border-gray-200 text-gray-900"
                             />
                             <Button
                               variant="ghost"
                               size="sm"
                               onClick={() => {
                                 const headers = Array.isArray(selectedNode.data?.webhook_headers) 
                                   ? selectedNode.data.webhook_headers 
                                   : Object.entries(selectedNode.data?.webhook_headers || {}).map(([k, v]) => ({key: k, value: v}));
                                 const newHeaders = headers.filter((_, i) => i !== idx);
                                 updateNode(selectedNode.id, { data: { ...selectedNode.data, webhook_headers: newHeaders } });
                               }}
                               className="text-red-400 hover:text-red-300"
                             >
                               <Trash2 size={16} />
                             </Button>
                           </div>
                         ))
                       )}
                    </div>
                  </div>

                  <div className="mt-4">
                    <Label className="text-gray-700 flex justify-between items-center">
                      Query Parameters
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => {
                          const params = selectedNode.data?.webhook_query_params || [];
                          updateNode(selectedNode.id, {
                            data: { ...selectedNode.data, webhook_query_params: [...params, { key: '', value: '' }] }
                          });
                        }}
                        className="text-blue-400 hover:text-blue-300 h-6 px-2"
                      >
                        <Plus size={14} className="mr-1" /> Add Parameter
                      </Button>
                    </Label>
                    <div className="space-y-2 mt-2">
                       {(selectedNode.data?.webhook_query_params || []).length === 0 ? (
                         <p className="text-xs text-gray-500 italic">No query parameters configured</p>
                       ) : (
                         (selectedNode.data?.webhook_query_params || []).map((param, idx) => (
                           <div key={idx} className="flex items-center gap-2">
                             <Input
                               placeholder="Key (e.g. startDate)"
                               value={param.key}
                               onChange={(e) => {
                                 const params = selectedNode.data?.webhook_query_params || [];
                                 const newParams = [...params];
                                 newParams[idx].key = e.target.value;
                                 updateNode(selectedNode.id, { data: { ...selectedNode.data, webhook_query_params: newParams } });
                               }}
                               className="bg-white border-gray-200 text-gray-900"
                             />
                             <Input
                               placeholder="Value (e.g. {{appointment_date_ms}})"
                               value={param.value}
                               onChange={(e) => {
                                 const params = selectedNode.data?.webhook_query_params || [];
                                 const newParams = [...params];
                                 newParams[idx].value = e.target.value;
                                 updateNode(selectedNode.id, { data: { ...selectedNode.data, webhook_query_params: newParams } });
                               }}
                               className="bg-white border-gray-200 text-gray-900"
                             />
                             <Button
                               variant="ghost"
                               size="sm"
                               onClick={() => {
                                 const params = selectedNode.data?.webhook_query_params || [];
                                 const newParams = params.filter((_, i) => i !== idx);
                                 updateNode(selectedNode.id, { data: { ...selectedNode.data, webhook_query_params: newParams } });
                               }}
                               className="text-red-400 hover:text-red-300"
                             >
                               <Trash2 size={16} />
                             </Button>
                           </div>
                         ))
                       )}
                    </div>
                  </div>

                  {selectedNode.data?.webhook_method !== 'GET' && (
                    <div className="mt-4">
                      <Label className="text-gray-700">Request Body (JSON)</Label>
                      <Textarea
                        value={selectedNode.data?.webhook_body || ''}
                        onChange={(e) => updateNode(selectedNode.id, {
                          data: { ...selectedNode.data, webhook_body: e.target.value }
                        })}
                        className="bg-white border-gray-200 text-gray-900 mt-2 font-mono text-sm"
                        rows={4}
                        placeholder='{"appointment_date": "{{appointment_date}}"}'
                      />
                      <p className="text-xs text-gray-500 mt-1">
                        Use {`{{variable_name}}`} for extracted variables.
                      </p>
                    </div>
                  )}

                  <div>
                    <Label className="text-gray-700">Timeout (seconds)</Label>
                    <Input
                      type="number"
                      value={selectedNode.data?.webhook_timeout || 10}
                      onChange={(e) => updateNode(selectedNode.id, {
                        data: { ...selectedNode.data, webhook_timeout: parseInt(e.target.value) || 10 }
                      })}
                      className="bg-white border-gray-200 text-gray-900 mt-2"
                      min="1"
                      max="30"
                    />
                  </div>

                  <div>
                    <Label className="text-gray-700">Response Variable Name</Label>
                    <Input
                      value={selectedNode.data?.response_variable || 'webhook_response'}
                      onChange={(e) => updateNode(selectedNode.id, {
                        data: { ...selectedNode.data, response_variable: e.target.value }
                      })}
                      className="bg-white border-gray-200 text-gray-900 mt-2"
                      placeholder="webhook_response"
                    />
                    <p className="text-xs text-gray-500 mt-1">
                      Store webhook response in this variable. Use in transitions to check response values.
                    </p>
                  </div>

                  {/* Example section */}
                  <div className="bg-white border border-gray-200 rounded-lg p-3 shadow-sm">
                    <p className="text-xs text-green-400 font-medium mb-1">💡 Example Use Case:</p>
                    <p className="text-xs text-gray-500">
                      1. Extract: appointment_date, appointment_time<br />
                      2. Send to webhook for availability check<br />
                      3. Add transitions: If webhook_response.available == true → confirm, If available == false → ask_different_date
                    </p>
                  </div>

                  {/* Webhook Tester Section */}
                  <div className="border-t border-gray-200 pt-4 mt-4">
                    <button
                      onClick={() => setShowWebhookTester(!showWebhookTester)}
                      className="flex items-center justify-between w-full text-left"
                    >
                      <h3 className="text-gray-900 font-semibold flex items-center gap-2">
                        <Play size={16} className="text-blue-400" />
                        Webhook Tester
                      </h3>
                      <span className="text-gray-500 text-sm">{showWebhookTester ? '▼' : '▶'}</span>
                    </button>

                    {showWebhookTester && (
                      <div className="mt-4 space-y-4">
                        <p className="text-xs text-gray-500">
                          Test your webhook with sample variable values before using it in calls.
                        </p>

                        {/* JSON Schema Variables - Detect and show variables from schema */}
                        {(() => {
                          const bodyTemplate = selectedNode.data?.webhook_body || '';
                          let schemaVars = [];
                          try {
                            const bodyObj = JSON.parse(bodyTemplate);
                            if (bodyObj && bodyObj.type === 'object' && bodyObj.properties) {
                              schemaVars = Object.entries(bodyObj.properties).map(([name, def]) => ({
                                name,
                                description: def.description || '',
                                type: def.type || 'string'
                              }));
                            }
                          } catch (e) { /* JSON parse failed - not a schema format */ }

                          if (schemaVars.length > 0) {
                            return (
                              <div className="bg-blue-900/30 rounded-lg p-3 space-y-3 border border-blue-700/50">
                                <Label className="text-blue-300 text-sm">📋 Schema Variables (from webhook body):</Label>
                                <p className="text-xs text-gray-500">Your webhook body uses JSON Schema format. Fill in test values for each property:</p>
                                {schemaVars.map((varDef, idx) => (
                                  <div key={idx} className="space-y-1">
                                    <div className="flex items-center gap-2">
                                      <span className="text-xs text-blue-400 min-w-[100px] font-medium">
                                        {varDef.name}
                                      </span>
                                      <Input
                                        value={webhookTestVariables[varDef.name] || ''}
                                        onChange={(e) => setWebhookTestVariables({
                                          ...webhookTestVariables,
                                          [varDef.name]: e.target.value
                                        })}
                                        className="bg-gray-50 border-gray-200 text-gray-900 text-sm flex-1"
                                        placeholder={varDef.description || `Enter test value for ${varDef.name}`}
                                      />
                                    </div>
                                    {varDef.description && (
                                      <p className="text-xs text-gray-500 ml-[108px]">{varDef.description}</p>
                                    )}
                                  </div>
                                ))}
                              </div>
                            );
                          }
                          return null;
                        })()}

                        {/* Variable inputs from extract_variables (Template format) */}
                        {(selectedNode.data?.extract_variables || []).length > 0 && (
                          <div className="bg-white/50 rounded-lg p-3 space-y-3">
                            <Label className="text-gray-700 text-sm">Test Variables (from Extract Variables):</Label>
                            {(selectedNode.data?.extract_variables || []).map((varDef, idx) => (
                              <div key={idx} className="flex items-center gap-2">
                                <span className="text-xs text-purple-400 min-w-[100px]">
                                  {`{{${extractionVariableName(varDef)}}}`}
                                </span>
                                <Input
                                  value={webhookTestVariables[extractionVariableName(varDef)] || ''}
                                  onChange={(e) => setWebhookTestVariables({
                                    ...webhookTestVariables,
                                    [extractionVariableName(varDef)]: e.target.value
                                  })}
                                  className="bg-gray-50 border-gray-200 text-gray-900 text-sm flex-1"
                                  placeholder={`Enter test value for ${extractionVariableName(varDef)}`}
                                />
                              </div>
                            ))}
                          </div>
                        )}

                        {/* Additional common variables */}
                        <div className="bg-white/50 rounded-lg p-3 space-y-3">
                          <Label className="text-gray-700 text-sm">Common Variables:</Label>
                          <div className="flex items-center gap-2">
                            <span className="text-xs text-purple-400 min-w-[100px]">{`{{user_message}}`}</span>
                            <Input
                              value={webhookTestVariables['user_message'] || ''}
                              onChange={(e) => setWebhookTestVariables({
                                ...webhookTestVariables,
                                user_message: e.target.value
                              })}
                              className="bg-gray-50 border-gray-200 text-gray-900 text-sm flex-1"
                              placeholder="Test user message"
                            />
                          </div>
                        </div>

                        {/* Custom variables section */}
                        <div className="bg-white/50 rounded-lg p-3 space-y-3">
                          <div className="flex items-center justify-between">
                            <Label className="text-gray-700 text-sm">Custom Variables:</Label>
                            <button
                              onClick={() => setCustomTestVariables([...customTestVariables, { name: '', value: '' }])}
                              className="flex items-center gap-1 text-xs text-blue-400 hover:text-blue-300"
                            >
                              <Plus size={14} />
                              Add Variable
                            </button>
                          </div>

                          {customTestVariables.map((customVar, idx) => (
                            <div key={idx} className="flex items-center gap-2">
                              <Input
                                value={customVar.name}
                                onChange={(e) => {
                                  const updated = [...customTestVariables];
                                  updated[idx].name = e.target.value;
                                  setCustomTestVariables(updated);
                                }}
                                className="bg-gray-50 border-gray-200 text-purple-400 text-sm w-28"
                                placeholder="var_name"
                              />
                              <span className="text-gray-500">=</span>
                              <Input
                                value={customVar.value}
                                onChange={(e) => {
                                  const updated = [...customTestVariables];
                                  updated[idx].value = e.target.value;
                                  setCustomTestVariables(updated);
                                }}
                                className="bg-gray-50 border-gray-200 text-gray-900 text-sm flex-1"
                                placeholder="test value"
                              />
                              <button
                                onClick={() => {
                                  const updated = customTestVariables.filter((_, i) => i !== idx);
                                  setCustomTestVariables(updated);
                                }}
                                className="text-red-400 hover:text-red-300 p-1"
                              >
                                <X size={14} />
                              </button>
                            </div>
                          ))}

                          {customTestVariables.length === 0 && (
                            <p className="text-xs text-gray-500 italic">
                              Click + to add custom variables like {`{{phone_number}}`}, {`{{customer_name}}`}, etc.
                            </p>
                          )}
                        </div>

                        {/* Test button */}
                        <Button
                          onClick={testWebhook}
                          disabled={webhookTestLoading || !selectedNode.data?.webhook_url}
                          className="w-full bg-blue-600 hover:bg-blue-700"
                        >
                          {webhookTestLoading ? (
                            <>
                              <span className="animate-spin mr-2">⏳</span>
                              Testing...
                            </>
                          ) : (
                            <>
                              <Play size={16} className="mr-2" />
                              Send Test Request
                            </>
                          )}
                        </Button>

                        {/* Response display */}
                        {webhookTestResponse && (
                          <div className={`rounded-lg p-3 ${webhookTestResponse.success ? 'bg-green-900/30 border border-green-700/50' : 'bg-red-900/30 border border-red-700/50'}`}>
                            <div className="flex items-center justify-between mb-2">
                              <span className={`text-sm font-medium ${webhookTestResponse.success ? 'text-green-400' : 'text-red-400'}`}>
                                {webhookTestResponse.success ? '✅ Success' : '❌ Failed'}
                              </span>
                              <span className="text-xs text-gray-500">
                                {webhookTestResponse.status && `Status: ${webhookTestResponse.status}`}
                                {webhookTestResponse.duration && ` • ${webhookTestResponse.duration}ms`}
                              </span>
                            </div>

                            {/* Request sent */}
                            <div className="mb-2">
                              <p className="text-xs text-gray-500 mb-1">Request:</p>
                              <pre className="text-xs bg-white p-2 rounded overflow-auto max-h-20 text-gray-700">
                                {webhookTestResponse.request?.method} {webhookTestResponse.request?.url}
                                {webhookTestResponse.request?.body && '\n\n' + JSON.stringify(webhookTestResponse.request.body, null, 2)}
                              </pre>
                            </div>

                            {/* Response or error */}
                            <div>
                              <p className="text-xs text-gray-500 mb-1">
                                {webhookTestResponse.success ? 'Response:' : 'Error:'}
                              </p>
                              <pre className="text-xs bg-white p-2 rounded overflow-auto max-h-32 text-gray-700">
                                {webhookTestResponse.success
                                  ? JSON.stringify(webhookTestResponse.data, null, 2)
                                  : webhookTestResponse.error
                                }
                              </pre>
                            </div>

                            {/* Variable mapping hint */}
                            {webhookTestResponse.success && (
                              <p className="text-xs text-gray-500 mt-2">
                                💡 Access response values in transitions using: {selectedNode.data?.response_variable || 'webhook_response'}.field_name
                              </p>
                            )}
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Transfer Call node settings */}
              {(selectedNode.type === 'call_transfer' || selectedNode.type === 'agent_transfer') && (
                <div className="space-y-4 border-t border-gray-200 pt-4">
                  <h3 className="text-gray-900 font-semibold">Transfer Settings</h3>

                  <div>
                    <Label className="text-gray-700">Transfer Type</Label>
                    <Select
                      value={selectedNode.data?.transfer_type || 'cold'}
                      onValueChange={(value) => updateNode(selectedNode.id, {
                        data: { ...selectedNode.data, transfer_type: value }
                      })}
                    >
                      <SelectTrigger className="bg-white border-gray-200 text-gray-900 mt-2">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="cold">Cold Transfer (Blind)</SelectItem>
                        <SelectItem value="warm">Warm Transfer (Announced)</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  <div>
                    <Label className="text-gray-700">Destination Type</Label>
                    <Select
                      value={selectedNode.data?.destination_type || 'phone'}
                      onValueChange={(value) => updateNode(selectedNode.id, {
                        data: { ...selectedNode.data, destination_type: value }
                      })}
                    >
                      <SelectTrigger className="bg-white border-gray-200 text-gray-900 mt-2">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="phone">Phone Number</SelectItem>
                        <SelectItem value="agent">Agent/Queue</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  <div>
                    <Label className="text-gray-700">
                      {selectedNode.data?.destination_type === 'phone' ? 'Phone Number' : 'Agent Name'}
                    </Label>
                    <Input
                      value={selectedNode.data?.destination || ''}
                      onChange={(e) => updateNode(selectedNode.id, {
                        data: { ...selectedNode.data, destination: e.target.value }
                      })}
                      className="bg-white border-gray-200 text-gray-900 mt-2"
                      placeholder={selectedNode.data?.destination_type === 'phone' ? '+1234567890' : 'sales-team'}
                    />
                  </div>

                  <div>
                    <Label className="text-gray-700">Transfer Message</Label>
                    <Textarea
                      value={selectedNode.data?.transfer_message || ''}
                      onChange={(e) => updateNode(selectedNode.id, {
                        data: { ...selectedNode.data, transfer_message: e.target.value }
                      })}
                      className="bg-white border-gray-200 text-gray-900 mt-2"
                      rows={2}
                      placeholder="Please hold while I transfer your call..."
                    />
                    <p className="text-xs text-gray-500 mt-1">
                      Message to play before transfer
                    </p>
                  </div>

                  <div>
                    <Label className="text-gray-700">Transfer Timeout (seconds)</Label>
                    <Input
                      type="number"
                      value={selectedNode.data?.timeout_secs || 30}
                      onChange={(e) => updateNode(selectedNode.id, {
                        data: { ...selectedNode.data, timeout_secs: parseInt(e.target.value) || 30 }
                      })}
                      className="bg-white border-gray-200 text-gray-900 mt-2"
                      min={5}
                      max={120}
                      placeholder="30"
                    />
                    <p className="text-xs text-gray-500 mt-1">
                      How long to ring the destination before giving up (5-120s)
                    </p>
                  </div>
                </div>
              )}

              {/* Collect Input node settings */}
              {selectedNode.type === 'collect_input' && (
                <div className="space-y-4 border-t border-gray-200 pt-4">
                  <h3 className="text-gray-900 font-semibold">Collect Input Settings</h3>

                  <div>
                    <Label className="text-gray-700">Variable Name</Label>
                    <Input
                      value={selectedNode.data?.variable_name || 'user_input'}
                      onChange={(e) => updateNode(selectedNode.id, {
                        data: { ...selectedNode.data, variable_name: e.target.value }
                      })}
                      className="bg-white border-gray-200 text-gray-900 mt-2"
                      placeholder="user_email"
                    />
                    <p className="text-xs text-gray-500 mt-1">
                      Name to store the collected value
                    </p>
                  </div>

                  <div>
                    <Label className="text-gray-700">Input Type</Label>
                    <Select
                      value={selectedNode.data?.input_type || 'text'}
                      onValueChange={(value) => updateNode(selectedNode.id, {
                        data: { ...selectedNode.data, input_type: value }
                      })}
                    >
                      <SelectTrigger className="bg-white border-gray-200 text-gray-900 mt-2">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="text">Text</SelectItem>
                        <SelectItem value="email">Email</SelectItem>
                        <SelectItem value="phone">Phone Number</SelectItem>
                        <SelectItem value="number">Number</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  <div>
                    <Label className="text-gray-700">Prompt Message</Label>
                    <Textarea
                      value={selectedNode.data?.prompt_message || ''}
                      onChange={(e) => updateNode(selectedNode.id, {
                        data: { ...selectedNode.data, prompt_message: e.target.value }
                      })}
                      className="bg-white border-gray-200 text-gray-900 mt-2"
                      rows={2}
                      placeholder="What to ask the user"
                    />
                    <p className="text-xs text-gray-500 mt-1">
                      What to ask the user for this input
                    </p>
                  </div>

                  <div>
                    <Label className="text-gray-700">Error Message</Label>
                    <Textarea
                      value={selectedNode.data?.error_message || ''}
                      onChange={(e) => updateNode(selectedNode.id, {
                        data: { ...selectedNode.data, error_message: e.target.value }
                      })}
                      className="bg-white border-gray-200 text-gray-900 mt-2"
                      rows={2}
                      placeholder="Message for invalid input"
                    />
                    <p className="text-xs text-gray-500 mt-1">
                      Message to show when input is invalid
                    </p>
                  </div>
                </div>
              )}

              {/* Send SMS node settings */}
              {selectedNode.type === 'send_sms' && (
                <div className="space-y-4 border-t border-gray-200 pt-4">
                  <h3 className="text-gray-900 font-semibold">Send SMS Settings</h3>

                  <div>
                    <Label className="text-gray-700">Phone Number</Label>
                    <Input
                      value={selectedNode.data?.phone_number || ''}
                      onChange={(e) => updateNode(selectedNode.id, {
                        data: { ...selectedNode.data, phone_number: e.target.value }
                      })}
                      className="bg-white border-gray-200 text-gray-900 mt-2"
                      placeholder="Enter phone or variable name"
                    />
                    <p className="text-xs text-gray-500 mt-1">
                      Phone number or variable reference (e.g., user_phone)
                    </p>
                  </div>

                  <div>
                    <Label className="text-gray-700">SMS Message</Label>
                    <Textarea
                      value={selectedNode.data?.sms_message || ''}
                      onChange={(e) => updateNode(selectedNode.id, {
                        data: { ...selectedNode.data, sms_message: e.target.value }
                      })}
                      className="bg-white border-gray-200 text-gray-900 mt-2"
                      rows={4}
                      placeholder="Enter your SMS message here"
                    />
                    <p className="text-xs text-gray-500 mt-1">
                      Use variables in your message (e.g., user_name)
                    </p>
                  </div>
                </div>
              )}

              {/* Logic Split node settings */}
              {selectedNode.type === 'logic_split' && (
                <div className="space-y-4 border-t border-gray-200 pt-4">
                  <h3 className="text-gray-900 font-semibold flex items-center gap-2">
                    <GitBranch className="w-4 h-4 text-pink-400" />
                    Logic Split Settings
                  </h3>

                  <div className="p-3 bg-blue-900/30 rounded-lg border border-blue-700/50">
                    <p className="text-xs text-blue-300">
                      💡 Create conditions to route calls based on extracted variables.
                      Conditions are evaluated in order - first match wins.
                    </p>
                  </div>

                  <div className="space-y-3">
                    <Label className="text-gray-700">Conditions</Label>
                    {selectedNode.data?.conditions?.map((condition, index) => (
                      <div key={condition.id} className="p-3 bg-gray-50 rounded-lg space-y-3 border border-gray-200">
                        <div className="flex items-center justify-between">
                          <span className="text-sm font-medium text-pink-400">Condition {index + 1}</span>
                          {selectedNode.data.conditions.length > 1 && (
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => {
                                const newConditions = selectedNode.data.conditions.filter((_, i) => i !== index);
                                updateNode(selectedNode.id, {
                                  data: { ...selectedNode.data, conditions: newConditions }
                                });
                              }}
                              className="text-red-400 hover:text-red-300 h-6 px-2"
                            >
                              <X className="w-3 h-3" />
                            </Button>
                          )}
                        </div>

                        {/* Variable Name - with suggestions from extracted variables */}
                        <div>
                          <Label className="text-xs text-gray-500 mb-1">Variable Name</Label>
                          <div className="flex gap-2">
                            <Input
                              value={condition.variable || ''}
                              onChange={(e) => {
                                const newConditions = [...selectedNode.data.conditions];
                                newConditions[index] = { ...condition, variable: e.target.value };
                                updateNode(selectedNode.id, {
                                  data: { ...selectedNode.data, conditions: newConditions }
                                });
                              }}
                              className="bg-white border-gray-200 text-gray-900 flex-1"
                              placeholder="e.g., Amount_Reference, user_name"
                            />
                            {/* Quick variable picker */}
                            {(() => {
                              // Collect all extracted variables from all nodes
                              const allVars = nodes.flatMap(n =>
                                (n.data?.extract_variables || []).map(extractionVariableName)
                              ).filter(Boolean);
                              const uniqueVars = [...new Set(allVars)];

                              if (uniqueVars.length > 0) {
                                return (
                                  <Select
                                    value=""
                                    onValueChange={(value) => {
                                      const newConditions = [...selectedNode.data.conditions];
                                      newConditions[index] = { ...condition, variable: value };
                                      updateNode(selectedNode.id, {
                                        data: { ...selectedNode.data, conditions: newConditions }
                                      });
                                    }}
                                  >
                                    <SelectTrigger className="bg-white border-gray-200 text-gray-900 w-24">
                                      <SelectValue placeholder="Pick" />
                                    </SelectTrigger>
                                    <SelectContent>
                                      {uniqueVars.map(varName => (
                                        <SelectItem key={varName} value={varName}>{varName}</SelectItem>
                                      ))}
                                    </SelectContent>
                                  </Select>
                                );
                              }
                              return null;
                            })()}
                          </div>
                        </div>

                        {/* Value Type Selector */}
                        <div>
                          <Label className="text-xs text-gray-500 mb-1">Value Type</Label>
                          <Select
                            value={condition.value_type || 'string'}
                            onValueChange={(value) => {
                              const newConditions = [...selectedNode.data.conditions];
                              // Reset operator to appropriate default when switching types
                              const defaultOperator = value === 'number' ? 'greater_than' : 'equals';
                              newConditions[index] = {
                                ...condition,
                                value_type: value,
                                operator: defaultOperator
                              };
                              updateNode(selectedNode.id, {
                                data: { ...selectedNode.data, conditions: newConditions }
                              });
                            }}
                          >
                            <SelectTrigger className="bg-white border-gray-200 text-gray-900">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="string">📝 Text/String</SelectItem>
                              <SelectItem value="number">🔢 Number</SelectItem>
                              <SelectItem value="existence">❓ Check Existence</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>

                        {/* Operator - changes based on value type */}
                        <div>
                          <Label className="text-xs text-gray-500 mb-1">Operator</Label>
                          <Select
                            value={condition.operator || 'equals'}
                            onValueChange={(value) => {
                              const newConditions = [...selectedNode.data.conditions];
                              newConditions[index] = { ...condition, operator: value };
                              updateNode(selectedNode.id, {
                                data: { ...selectedNode.data, conditions: newConditions }
                              });
                            }}
                          >
                            <SelectTrigger className="bg-white border-gray-200 text-gray-900">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              {(condition.value_type === 'number') ? (
                                <>
                                  <SelectItem value="greater_than">&gt; Greater Than</SelectItem>
                                  <SelectItem value="greater_than_or_equal">≥ Greater Than or Equal</SelectItem>
                                  <SelectItem value="less_than">&lt; Less Than</SelectItem>
                                  <SelectItem value="less_than_or_equal">≤ Less Than or Equal</SelectItem>
                                  <SelectItem value="equals">= Equals</SelectItem>
                                  <SelectItem value="not_equals">≠ Not Equals</SelectItem>
                                </>
                              ) : (condition.value_type === 'existence') ? (
                                <>
                                  <SelectItem value="exists">✓ Variable Exists (has value)</SelectItem>
                                  <SelectItem value="not_exists">✗ Variable Does Not Exist</SelectItem>
                                </>
                              ) : (
                                <>
                                  <SelectItem value="equals">= Equals (exact match)</SelectItem>
                                  <SelectItem value="not_equals">≠ Not Equals</SelectItem>
                                  <SelectItem value="contains">⊃ Contains (partial match)</SelectItem>
                                  <SelectItem value="starts_with">▶ Starts With</SelectItem>
                                  <SelectItem value="ends_with">◀ Ends With</SelectItem>
                                </>
                              )}
                            </SelectContent>
                          </Select>
                        </div>

                        {/* Value Input - different based on type */}
                        {condition.value_type !== 'existence' && (
                          <div>
                            <Label className="text-xs text-gray-500 mb-1">
                              {condition.value_type === 'number' ? 'Compare Value (Number)' : 'Compare Value (Text)'}
                            </Label>
                            <Input
                              type={condition.value_type === 'number' ? 'number' : 'text'}
                              value={condition.value || ''}
                              onChange={(e) => {
                                const newConditions = [...selectedNode.data.conditions];
                                newConditions[index] = { ...condition, value: e.target.value };
                                updateNode(selectedNode.id, {
                                  data: { ...selectedNode.data, conditions: newConditions }
                                });
                              }}
                              className="bg-white border-gray-200 text-gray-900"
                              placeholder={condition.value_type === 'number' ? 'e.g., 8000' : 'e.g., yes, interested'}
                            />
                            {condition.value_type === 'number' && (
                              <p className="text-xs text-gray-500 mt-1">
                                💡 Enter a plain number. Supports: 8000, 10000, etc.
                              </p>
                            )}
                          </div>
                        )}

                        {/* Next Node */}
                        <div>
                          <Label className="text-xs text-gray-500 mb-1">→ Then Go To</Label>
                          <Select
                            value={condition.nextNode || ''}
                            onValueChange={(value) => {
                              const newConditions = [...selectedNode.data.conditions];
                              newConditions[index] = { ...condition, nextNode: value };
                              updateNode(selectedNode.id, {
                                data: { ...selectedNode.data, conditions: newConditions }
                              });
                            }}
                          >
                            <SelectTrigger className="bg-white border-gray-200 text-gray-900">
                              <SelectValue placeholder="Select next node" />
                            </SelectTrigger>
                            <SelectContent>
                              {nodes.filter(n => n.id !== selectedNode.id).map(node => (
                                <SelectItem key={node.id} value={node.id}>
                                  {node.label}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>

                        {/* Condition Preview */}
                        <div className="p-2 bg-white/50 rounded text-xs text-gray-500 border border-gray-200/50">
                          <span className="text-gray-500">Preview: </span>
                          <span className="text-purple-400">{condition.variable || '[variable]'}</span>
                          {' '}
                          <span className="text-pink-400">
                            {condition.operator === 'greater_than' ? '>' :
                              condition.operator === 'greater_than_or_equal' ? '>=' :
                                condition.operator === 'less_than' ? '<' :
                                  condition.operator === 'less_than_or_equal' ? '<=' :
                                    condition.operator === 'equals' ? '=' :
                                      condition.operator === 'not_equals' ? '≠' :
                                        condition.operator === 'contains' ? 'contains' :
                                          condition.operator === 'starts_with' ? 'starts with' :
                                            condition.operator === 'ends_with' ? 'ends with' :
                                              condition.operator === 'exists' ? 'exists' :
                                                condition.operator === 'not_exists' ? 'does not exist' :
                                                  condition.operator}
                          </span>
                          {' '}
                          {condition.value_type !== 'existence' && (
                            <span className="text-green-400">{condition.value || '[value]'}</span>
                          )}
                        </div>
                      </div>
                    ))}

                    <Button
                      onClick={() => {
                        const newCondition = {
                          id: Date.now().toString(),
                          variable: '',
                          value_type: 'string',
                          operator: 'equals',
                          value: '',
                          nextNode: ''
                        };
                        updateNode(selectedNode.id, {
                          data: {
                            ...selectedNode.data,
                            conditions: [...(selectedNode.data.conditions || []), newCondition]
                          }
                        });
                      }}
                      size="sm"
                      className="w-full bg-pink-600 hover:bg-pink-700"
                    >
                      <Plus className="w-4 h-4 mr-2" /> Add Condition
                    </Button>
                  </div>

                  <div className="pt-2 border-t border-gray-200">
                    <Label className="text-gray-700">Default Path (No Conditions Match)</Label>
                    <p className="text-xs text-gray-500 mb-2">Where to go if none of the conditions above match</p>
                    <Select
                      value={selectedNode.data?.default_next_node || ''}
                      onValueChange={(value) => updateNode(selectedNode.id, {
                        data: { ...selectedNode.data, default_next_node: value }
                      })}
                    >
                      <SelectTrigger className="bg-white border-gray-200 text-gray-900">
                        <SelectValue placeholder="Select default node" />
                      </SelectTrigger>
                      <SelectContent>
                        {nodes.filter(n => n.id !== selectedNode.id).map(node => (
                          <SelectItem key={node.id} value={node.id}>{node.label}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              )}

              {/* Press Digit node settings */}
              {selectedNode.type === 'press_digit' && (
                <div className="space-y-4 border-t border-gray-200 pt-4">
                  <h3 className="text-gray-900 font-semibold">Press Digit (DTMF) Settings</h3>

                  <div>
                    <Label className="text-gray-700">Prompt Message</Label>
                    <Textarea
                      value={selectedNode.data?.prompt_message || ''}
                      onChange={(e) => updateNode(selectedNode.id, {
                        data: { ...selectedNode.data, prompt_message: e.target.value }
                      })}
                      className="bg-white border-gray-200 text-gray-900 mt-2"
                      rows={2}
                      placeholder="Please press a digit"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label className="text-gray-700">Digit Mappings</Label>
                    <p className="text-xs text-gray-500">Map each digit to a next node</p>

                    {['1', '2', '3', '4', '5', '6', '7', '8', '9', '0', '*', '#'].map(digit => (
                      <div key={digit} className="flex items-center gap-2">
                        <span className="text-gray-900 font-mono w-8">{digit}</span>
                        <Select
                          value={selectedNode.data?.digit_mappings?.[digit] || 'none'}
                          onValueChange={(value) => {
                            const newMappings = { ...selectedNode.data?.digit_mappings };
                            if (value && value !== 'none') {
                              newMappings[digit] = value;
                            } else {
                              delete newMappings[digit];
                            }
                            updateNode(selectedNode.id, {
                              data: { ...selectedNode.data, digit_mappings: newMappings }
                            });
                          }}
                        >
                          <SelectTrigger className="bg-white border-gray-200 text-gray-900 flex-1">
                            <SelectValue placeholder="Select node" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="none">None</SelectItem>
                            {nodes.filter(n => n.id !== selectedNode.id).map(node => (
                              <SelectItem key={node.id} value={node.id}>{node.label}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Extract Variable node settings */}
              {selectedNode.type === 'extract_variable' && (
                <div className="space-y-4 border-t border-gray-200 pt-4">
                  <h3 className="text-gray-900 font-semibold">Extract Variable Settings</h3>

                  <div>
                    <Label className="text-gray-700">Variable Name</Label>
                    <Input
                      value={selectedNode.data?.variable_name || ''}
                      onChange={(e) => updateNode(selectedNode.id, {
                        data: { ...selectedNode.data, variable_name: e.target.value }
                      })}
                      className="bg-white border-gray-200 text-gray-900 mt-2"
                      placeholder="customer_name"
                    />
                    <p className="text-xs text-gray-500 mt-1">
                      Name to store the extracted value
                    </p>
                  </div>

                  <div>
                    <Label className="text-gray-700">Extraction Prompt</Label>
                    <Textarea
                      value={selectedNode.data?.extraction_prompt || ''}
                      onChange={(e) => updateNode(selectedNode.id, {
                        data: { ...selectedNode.data, extraction_prompt: e.target.value }
                      })}
                      className="bg-white border-gray-200 text-gray-900 mt-2"
                      rows={3}
                      placeholder="Extract the customer name from the message"
                    />
                    <p className="text-xs text-gray-500 mt-1">
                      Instructions for AI on what to extract
                    </p>
                  </div>
                </div>
              )}

              {/* End Call node settings */}
              {selectedNode.type === 'ending' && (
                <div className="space-y-4 border-t border-gray-200 pt-4">
                  <div className="bg-red-500/10 border border-red-500/30 rounded-lg p-4">
                    <p className="text-gray-900 font-semibold mb-2">🛑 End Call</p>
                    <p className="text-sm text-gray-500">
                      This node terminates the call. No transitions allowed.
                    </p>
                  </div>

                  {/* Auto End Toggle */}
                  <div className="border border-gray-200 rounded-lg p-4 bg-white shadow-sm">
                    <div className="flex items-start gap-3">
                      <input
                        type="checkbox"
                        checked={selectedNode.data.auto_end !== false}
                        onChange={(e) => updateNode(selectedNode.id, {
                          data: { ...selectedNode.data, auto_end: e.target.checked }
                        })}
                        className="mt-1 w-4 h-4 text-red-600 bg-white border-gray-200 rounded focus:ring-red-500"
                        id="auto-end-toggle"
                      />
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-1">
                          <label htmlFor="auto-end-toggle" className="text-gray-900 font-medium text-sm cursor-pointer">
                            Auto End (hang up immediately)
                          </label>
                        </div>
                        <p className="text-xs text-gray-500">
                          If unchecked, the AI will listen for a final user response before hanging up.
                        </p>
                      </div>
                    </div>
                  </div>

                  <div>
                    <Label className="text-gray-700">Goodbye Message Mode</Label>
                    <div className="grid grid-cols-2 gap-2 mt-2">
                      <div
                        onClick={() => updateNode(selectedNode.id, {
                          data: { ...selectedNode.data, mode: 'script' }
                        })}
                        className={`p-3 rounded-lg border-2 cursor-pointer transition-all ${selectedNode.data.mode === 'script'
                          ? 'border-blue-500 bg-blue-500/10'
                          : 'border-gray-200 hover:border-gray-600'
                          }`}
                      >
                        <div className="text-center">
                          <div className="text-2xl mb-1">📝</div>
                          <div className="text-gray-900 font-semibold text-sm">Script</div>
                          <div className="text-gray-500 text-xs mt-1">Exact words</div>
                        </div>
                      </div>
                      <div
                        onClick={() => updateNode(selectedNode.id, {
                          data: { ...selectedNode.data, mode: 'prompt' }
                        })}
                        className={`p-3 rounded-lg border-2 cursor-pointer transition-all ${selectedNode.data.mode === 'prompt'
                          ? 'border-purple-500 bg-purple-500/10'
                          : 'border-gray-200 hover:border-gray-600'
                          }`}
                      >
                        <div className="text-center">
                          <div className="text-2xl mb-1">🤖</div>
                          <div className="text-gray-900 font-semibold text-sm">Prompt</div>
                          <div className="text-gray-500 text-xs mt-1">AI generates</div>
                        </div>
                      </div>
                    </div>
                  </div>

                  <div>
                    <div className="flex items-center justify-between mb-2">
                      <Label className="text-gray-700">
                        {selectedNode.data.mode === 'script' ? 'Goodbye Script' : 'Goodbye Instructions'}
                      </Label>
                      <Button
                        onClick={handleOptimizeClick}
                        size="sm"
                        className="bg-purple-600 hover:bg-purple-700 text-gray-900"
                      >
                        <Sparkles size={14} className="mr-1" />
                        Optimize with AI
                      </Button>
                    </div>
                    <Textarea
                      value={selectedNode.data.content || ''}
                      onChange={(e) => updateNode(selectedNode.id, {
                        data: { ...selectedNode.data, content: e.target.value }
                      })}
                      className="bg-white border-gray-200 text-gray-900"
                      rows={4}
                      placeholder={selectedNode.data.mode === 'script'
                        ? "e.g., 'Thank you for calling. Goodbye!'"
                        : "e.g., 'Thank the user and wish them a great day'"}
                    />
                    <p className="text-xs text-gray-500 mt-1">
                      {selectedNode.data.mode === 'script'
                        ? "AI will say these exact words before ending the call"
                        : "AI will generate a natural goodbye based on these instructions"}
                    </p>
                  </div>
                </div>
              )}

              {/* Wait (Inbound Webhook) node settings */}
              {selectedNode.type === 'inbound_webhook' && (
                <div className="space-y-4 border-t border-gray-200 pt-4">
                  <h3 className="text-gray-900 font-semibold">Wait / Inbound Webhook Settings</h3>
                  
                  <div className="bg-indigo-900/30 border border-indigo-700/50 rounded-lg p-3 text-sm text-indigo-200">
                    This node pauses the flow progression until an external system hits: <br/>
                    <code className="bg-indigo-900/60 px-1 py-0.5 rounded text-xs mt-1 inline-block">POST /api/calls/{'{call_id}'}/webhook_trigger</code>
                  </div>
                  
                  <div>
                    <Label className="text-gray-700">Timeout Duration (seconds)</Label>
                    <Input
                      type="number"
                      min={10}
                      value={selectedNode.data.timeout_duration || 60}
                      onChange={(e) => updateNode(selectedNode.id, {
                        data: { ...selectedNode.data, timeout_duration: parseInt(e.target.value) }
                      })}
                      className="bg-white border-gray-200 text-gray-900 mt-2"
                    />
                    <p className="text-xs text-gray-500 mt-1">Maximum time to wait before triggering timeout transition.</p>
                  </div>
                  
                  <div>
                    <Label className="text-gray-700">Initial Wait Message</Label>
                    <Textarea
                      value={selectedNode.data.wait_message || ''}
                      onChange={(e) => updateNode(selectedNode.id, {
                        data: { ...selectedNode.data, wait_message: e.target.value }
                      })}
                      className="bg-white border-gray-200 text-gray-900 mt-2"
                      rows={2}
                      placeholder="e.g. Give me just a second to request that..."
                    />
                    <p className="text-xs text-gray-500 mt-1">Spoken immediately upon entering this node.</p>
                  </div>
                  
                  <div>
                    <Label className="text-gray-700">Wait Prompt Context</Label>
                    <Textarea
                      value={selectedNode.data.prompt_context || ''}
                      onChange={(e) => updateNode(selectedNode.id, {
                        data: { ...selectedNode.data, prompt_context: e.target.value }
                      })}
                      className="bg-white border-gray-200 text-gray-900 mt-2"
                      rows={3}
                      placeholder="Instructions for the AI if the user talks while waiting."
                    />
                    <p className="text-xs text-gray-500 mt-1">How the AI should respond to chitchat without progressing.</p>
                  </div>
                </div>
              )}

              {/* Time Converter node settings */}
              {selectedNode.type === 'time_converter' && (
                <div className="space-y-4 border-t border-gray-200 pt-4">
                  <h3 className="text-gray-900 font-semibold flex items-center gap-2">
                    <Clock size={18} style={{ color: '#0ea5e9' }} />
                    Time Converter Settings
                  </h3>

                  {/* Info Tooltip - click to expand */}
                  <div className="rounded-lg border border-sky-600/50 overflow-hidden">
                    <button
                      onClick={() => {
                        const el = document.getElementById('time-converter-info');
                        if (el) el.classList.toggle('hidden');
                        const chevUp = document.getElementById('tc-chevron-up');
                        const chevDown = document.getElementById('tc-chevron-down');
                        if (chevUp && chevDown) {
                          chevUp.classList.toggle('hidden');
                          chevDown.classList.toggle('hidden');
                        }
                      }}
                      className="w-full flex items-center justify-between p-3 bg-sky-900/40 hover:bg-sky-900/60 transition-colors text-left"
                    >
                      <div className="flex items-center gap-2">
                        <Info size={16} className="text-sky-400" />
                        <span className="text-sky-300 font-semibold text-sm">How to use this node</span>
                      </div>
                      <div>
                        <ChevronDown id="tc-chevron-down" size={16} className="text-sky-400" />
                        <ChevronUp id="tc-chevron-up" size={16} className="text-sky-400 hidden" />
                      </div>
                    </button>
                    <div id="time-converter-info" className="hidden p-4 bg-sky-950/40 border-t border-sky-700/40 space-y-3">
                      <div>
                        <p className="text-sky-200 text-sm font-semibold mb-1">What it does</p>
                        <p className="text-gray-700 text-xs leading-relaxed">
                          This node silently converts raw ISO 8601 calendar time slots (from GHL or any calendar API) into
                          human-readable format in the caller's timezone. <strong>No speech is generated</strong> — it processes
                          data and auto-transitions to the next node instantly.
                        </p>
                      </div>
                      <div>
                        <p className="text-sky-200 text-sm font-semibold mb-1">Where to place it</p>
                        <p className="text-gray-700 text-xs leading-relaxed">
                          Place this node <strong>after</strong> your Calendar Availability webhook (Function node) and
                          <strong> before</strong> your scheduling conversation node. The typical flow is:
                        </p>
                        <div className="mt-2 bg-white/60 rounded p-2 text-xs text-gray-700 font-mono">
                          Ask Timezone → Calendar Webhook → <span className="text-sky-400 font-bold">Time Converter</span> → Ask for Callback Range → Schedule Time
                        </div>
                      </div>
                      <div>
                        <p className="text-sky-200 text-sm font-semibold mb-1">How the input data works</p>
                        <p className="text-gray-700 text-xs leading-relaxed">
                          The calendar webhook stores slot data as session variables keyed by date.
                          For example, after the webhook fires, you'll have variables like:
                        </p>
                        <div className="mt-1 bg-white/60 rounded p-2 text-xs text-gray-500 font-mono">
                          2026-04-22: {'{'} slots: ["2026-04-22T11:30:00-04:00", "2026-04-22T12:00:00-04:00", ...] {'}'}<br/>
                          2026-04-23: {'{'} slots: ["2026-04-23T11:30:00-04:00", ...] {'}'}
                        </div>
                        <p className="text-gray-700 text-xs leading-relaxed mt-1">
                          This node automatically scans session variables for date-keyed entries (YYYY-MM-DD format).
                          You can also specify a <strong>Source Variable</strong> if your data is stored in a single variable.
                        </p>
                      </div>
                      <div>
                        <p className="text-sky-200 text-sm font-semibold mb-1">Output format</p>
                        <p className="text-gray-700 text-xs leading-relaxed">
                          The converted result is stored in your Output Variable (default: <code className="bg-gray-50 px-1 rounded">convertedAvailability</code>).
                          The format looks like:
                        </p>
                        <div className="mt-1 bg-white/60 rounded p-2 text-xs text-green-400 font-mono">
                          Today: 11:30 AM, 12:00 PM, 1:00 PM, 2:00 PM<br/>
                          Tomorrow: 11:30 AM, 12:00 PM, 1:00 PM<br/>
                          Friday: 12:30 PM, 1:00 PM, 2:00 PM
                        </div>
                      </div>
                      <div>
                        <p className="text-sky-200 text-sm font-semibold mb-1">Using the output downstream</p>
                        <p className="text-gray-700 text-xs leading-relaxed">
                          In your scheduling conversation node, reference <code className="bg-gray-50 px-1 rounded">{'{{'}convertedAvailability{'}}'}</code> in the
                          prompt to give the LLM access to the available time slots. Example prompt snippet:
                        </p>
                        <div className="mt-1 bg-white/60 rounded p-2 text-xs text-gray-700 font-mono">
                          "Available slots in the caller's timezone:<br/>
                          {'{{'}convertedAvailability{'}}'}<br/>
                          Only offer times that appear in this list."
                        </div>
                      </div>
                      <div>
                        <p className="text-sky-200 text-sm font-semibold mb-1">Timezone mapping</p>
                        <p className="text-gray-700 text-xs leading-relaxed">
                          Automatically maps common timezone names: <strong>Eastern, Central, Mountain, Pacific, Alaska, Hawaii</strong> (and abbreviations like EST, CST, PST, EDT, CDT, PDT, etc.)
                          to proper IANA timezone identifiers. Full IANA names (e.g., America/Chicago) also work.
                        </p>
                      </div>
                    </div>
                  </div>

                  <div>
                    <Label className="text-gray-700">Source Variable <span className="text-gray-500">(optional)</span></Label>
                    <Input
                      value={selectedNode.data.source_variable || ''}
                      onChange={(e) => updateNode(selectedNode.id, {
                        data: { ...selectedNode.data, source_variable: e.target.value }
                      })}
                      className="bg-white border-gray-200 text-gray-900 mt-2"
                      placeholder="Leave empty to auto-detect date-keyed variables"
                    />
                    <p className="text-xs text-gray-500 mt-1">If your calendar data is in a single variable, enter its name. Otherwise leave empty — the node auto-scans for YYYY-MM-DD keyed variables.</p>
                  </div>

                  <div>
                    <Label className="text-gray-700">Output Variable</Label>
                    <Input
                      value={selectedNode.data.output_variable || 'convertedAvailability'}
                      onChange={(e) => updateNode(selectedNode.id, {
                        data: { ...selectedNode.data, output_variable: e.target.value }
                      })}
                      className="bg-white border-gray-200 text-gray-900 mt-2"
                      placeholder="convertedAvailability"
                    />
                    <p className="text-xs text-gray-500 mt-1">The session variable where converted slots will be stored. Reference this in your scheduling node with {'{{'}variableName{'}}'}.</p>
                  </div>

                  <div>
                    <Label className="text-gray-700">Timezone Variable</Label>
                    <Input
                      value={selectedNode.data.timezone_variable || 'timeZone'}
                      onChange={(e) => updateNode(selectedNode.id, {
                        data: { ...selectedNode.data, timezone_variable: e.target.value }
                      })}
                      className="bg-white border-gray-200 text-gray-900 mt-2"
                      placeholder="timeZone"
                    />
                    <p className="text-xs text-gray-500 mt-1">Session variable holding the caller's timezone (e.g., "Eastern", "Pacific", "CST").</p>
                  </div>

                  <div className="bg-white border border-gray-200 rounded-lg p-3 shadow-sm mt-2">
                    <p className="text-xs text-sky-300">
                      ⚡ <strong>Silent node</strong> — no speech is generated, no LLM is called. Conversion happens in ~1ms and auto-transitions to the next node.
                    </p>
                  </div>
                </div>
              )}

              {/* Stripe Payment node settings */}
              {selectedNode.type === 'stripe_payment' && (
                <div className="space-y-4 border-t border-gray-200 pt-4">
                  <h3 className="text-gray-900 font-semibold flex items-center gap-2">
                    <CreditCard size={18} style={{ color: '#6366f1' }} />
                    Stripe Payment Settings
                  </h3>

                  <div className="bg-indigo-900/10 border border-indigo-700/30 rounded-lg p-3">
                    <p className="text-xs text-indigo-800">
                      <strong>Note:</strong> This node does not collect the variables. Place it after a Conversation node that uses Variable Extraction to grab the credit card details.
                    </p>
                  </div>

                  <div>
                    <Label className="text-gray-700">Amount to Charge (in cents or variable)</Label>
                    <Input
                      value={selectedNode.data.amount_variable || 'amount'}
                      onChange={(e) => updateNode(selectedNode.id, {
                        data: { ...selectedNode.data, amount_variable: e.target.value }
                      })}
                      className="bg-white border-gray-200 text-gray-900 mt-2"
                      placeholder="e.g. 1000 or amount"
                    />
                  </div>

                  <div>
                    <Label className="text-gray-700">Currency</Label>
                    <Select
                      value={selectedNode.data.currency || 'usd'}
                      onValueChange={(value) => updateNode(selectedNode.id, {
                        data: { ...selectedNode.data, currency: value }
                      })}
                    >
                      <SelectTrigger className="bg-white border-gray-200 text-gray-900 mt-2">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="usd">USD ($)</SelectItem>
                        <SelectItem value="eur">EUR (€)</SelectItem>
                        <SelectItem value="gbp">GBP (£)</SelectItem>
                        <SelectItem value="cad">CAD ($)</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="pt-2">
                    <h4 className="text-sm font-semibold text-gray-900 mb-2">Variable Mapping</h4>
                    <p className="text-xs text-gray-500 mb-4">Map the variables you extracted in the previous node to the required Stripe fields.</p>
                    
                    <div className="space-y-3">
                      <div>
                        <Label className="text-xs text-gray-700">Card Number Variable</Label>
                        <Input
                          value={selectedNode.data.card_number_variable || 'cardNumber'}
                          onChange={(e) => updateNode(selectedNode.id, {
                            data: { ...selectedNode.data, card_number_variable: e.target.value }
                          })}
                          className="bg-white border-gray-200 text-gray-900 mt-1 h-8"
                        />
                      </div>
                      
                      <div className="grid grid-cols-2 gap-2">
                        <div>
                          <Label className="text-xs text-gray-700">Exp Month Var</Label>
                          <Input
                            value={selectedNode.data.exp_month_variable || 'expMonth'}
                            onChange={(e) => updateNode(selectedNode.id, {
                              data: { ...selectedNode.data, exp_month_variable: e.target.value }
                            })}
                            className="bg-white border-gray-200 text-gray-900 mt-1 h-8"
                          />
                        </div>
                        <div>
                          <Label className="text-xs text-gray-700">Exp Year Var</Label>
                          <Input
                            value={selectedNode.data.exp_year_variable || 'expYear'}
                            onChange={(e) => updateNode(selectedNode.id, {
                              data: { ...selectedNode.data, exp_year_variable: e.target.value }
                            })}
                            className="bg-white border-gray-200 text-gray-900 mt-1 h-8"
                          />
                        </div>
                      </div>
                      
                      <div>
                        <Label className="text-xs text-gray-700">CVC Variable</Label>
                        <Input
                          value={selectedNode.data.cvc_variable || 'cvc'}
                          onChange={(e) => updateNode(selectedNode.id, {
                            data: { ...selectedNode.data, cvc_variable: e.target.value }
                          })}
                          className="bg-white border-gray-200 text-gray-900 mt-1 h-8"
                        />
                      </div>
                    </div>
                  </div>

                  <div>
                    <div className="flex items-center gap-2 mt-4">
                      <input
                        type="checkbox"
                        checked={selectedNode.data.wait_for_result !== false}
                        onChange={(e) => updateNode(selectedNode.id, {
                          data: { ...selectedNode.data, wait_for_result: e.target.checked }
                        })}
                        className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                      />
                      <Label className="text-gray-700">Speak wait message while processing</Label>
                    </div>
                    {selectedNode.data.wait_for_result !== false && (
                      <Input
                        value={selectedNode.data.wait_message || 'Please wait a moment while I process your payment.'}
                        onChange={(e) => updateNode(selectedNode.id, {
                          data: { ...selectedNode.data, wait_message: e.target.value }
                        })}
                        className="bg-white border-gray-200 text-gray-900 mt-2"
                        placeholder="Please wait a moment while I process your payment."
                      />
                    )}
                  </div>
                </div>
              )}

              {/* Conversation node settings */}
              {selectedNode.type === 'conversation' && (
                <div className="space-y-4 border-t border-gray-200 pt-4">

                  <div>
                    <Label className="text-gray-700">Response Mode</Label>
                    <div className="grid grid-cols-2 gap-2 mt-2">
                      <div
                        onClick={() => updateNode(selectedNode.id, {
                          data: { ...selectedNode.data, mode: 'script' }
                        })}
                        className={`p-3 rounded-lg border-2 cursor-pointer transition-all ${selectedNode.data.mode === 'script'
                          ? 'border-blue-500 bg-blue-500/10'
                          : 'border-gray-200 hover:border-gray-600'
                          }`}
                      >
                        <div className="text-center">
                          <div className="text-2xl mb-1">📝</div>
                          <div className="text-gray-900 font-semibold text-sm">Script</div>
                          <div className="text-gray-500 text-xs mt-1">Exact words</div>
                        </div>
                      </div>
                      <div
                        onClick={() => updateNode(selectedNode.id, {
                          data: { ...selectedNode.data, mode: 'prompt' }
                        })}
                        className={`p-3 rounded-lg border-2 cursor-pointer transition-all ${selectedNode.data.mode === 'prompt'
                          ? 'border-purple-500 bg-purple-500/10'
                          : 'border-gray-200 hover:border-gray-600'
                          }`}
                      >
                        <div className="text-center">
                          <div className="text-2xl mb-1">🤖</div>
                          <div className="text-gray-900 font-semibold text-sm">Prompt</div>
                          <div className="text-gray-500 text-xs mt-1">AI generates</div>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Multi-LLM Processing Toggle */}
                  <div className="border border-gray-200 rounded-lg p-4 bg-white shadow-sm">
                    <div className="flex items-start gap-3">
                      <input
                        type="checkbox"
                        checked={selectedNode.data.use_parallel_llm || false}
                        onChange={(e) => updateNode(selectedNode.id, {
                          data: { ...selectedNode.data, use_parallel_llm: e.target.checked }
                        })}
                        className="mt-1 w-4 h-4 text-purple-600 bg-white border-gray-200 rounded focus:ring-purple-500"
                      />
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-1">
                          <Zap className="w-4 h-4 text-purple-400" />
                          <label className="text-gray-900 font-medium text-sm cursor-pointer" onClick={(e) => {
                            e.preventDefault();
                            updateNode(selectedNode.id, {
                              data: { ...selectedNode.data, use_parallel_llm: !selectedNode.data.use_parallel_llm }
                            });
                          }}>
                            Use Multi-LLM Processing
                          </label>
                        </div>
                        <p className="text-xs text-purple-300 leading-relaxed">
                          ⚡ <strong>45% faster</strong> - Uses specialized AI workers in parallel for complex nodes
                          <br />
                          🎯 <strong>Best for:</strong> Objection handling, qualification, KB lookups
                          <br />
                          💰 <strong>Cost:</strong> Same as regular processing
                        </p>
                      </div>
                    </div>
                    {selectedNode.data.use_parallel_llm && (
                      <div className="mt-3 p-2 bg-blue-900/20 rounded border border-blue-700/30">
                        <p className="text-xs text-blue-300">
                          <strong>How it works:</strong> Intent Classifier + DISC Analyzer run simultaneously (~400ms), providing better context to main LLM for faster, more accurate responses.
                        </p>
                      </div>
                    )}
                  </div>

                  {/* Q&A Mode - Skip Mandatory Pre-check */}
                  <div className="border border-gray-200 rounded-lg p-4 bg-white shadow-sm">
                    <div className="flex items-start gap-3">
                      <input
                        type="checkbox"
                        checked={selectedNode.data.skip_mandatory_precheck || false}
                        onChange={(e) => updateNode(selectedNode.id, {
                          data: { ...selectedNode.data, skip_mandatory_precheck: e.target.checked }
                        })}
                        className="mt-1 w-4 h-4 text-cyan-600 bg-white border-gray-200 rounded focus:ring-cyan-500"
                      />
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-1">
                          <span className="text-cyan-400">❓</span>
                          <label className="text-gray-900 font-medium text-sm cursor-pointer" onClick={(e) => {
                            e.preventDefault();
                            updateNode(selectedNode.id, {
                              data: { ...selectedNode.data, skip_mandatory_precheck: !selectedNode.data.skip_mandatory_precheck }
                            });
                          }}>
                            Q&A Mode (Answer First)
                          </label>
                        </div>
                        <p className="text-xs text-cyan-300 leading-relaxed">
                          📚 <strong>For Q&A/Knowledge Base nodes</strong> - Answer the user's question first
                          <br />
                          🔄 <strong>Behavior:</strong> Skip mandatory variable checks until transition evaluation
                          <br />
                          ✅ <strong>Use when:</strong> User asks questions that should be answered before collecting info
                        </p>
                      </div>
                    </div>
                  </div>

                  {/* Dynamic Rephrase - Only for Script Mode */}
                  {selectedNode.data.mode === 'script' && (
                    <div className="border border-gray-200 rounded-lg p-4 bg-white shadow-sm">
                      <div className="flex items-start gap-3">
                        <input
                          type="checkbox"
                          checked={selectedNode.data.dynamic_rephrase || false}
                          onChange={(e) => updateNode(selectedNode.id, {
                            data: { ...selectedNode.data, dynamic_rephrase: e.target.checked }
                          })}
                          className="mt-1 w-4 h-4 text-orange-600 bg-white border-gray-200 rounded focus:ring-orange-500"
                        />
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-1">
                            <span className="text-orange-400">🔄</span>
                            <label className="text-gray-900 font-medium text-sm cursor-pointer" onClick={(e) => {
                              e.preventDefault();
                              updateNode(selectedNode.id, {
                                data: { ...selectedNode.data, dynamic_rephrase: !selectedNode.data.dynamic_rephrase }
                              });
                            }}>
                              Dynamic Rephrase on Retry
                            </label>
                          </div>
                          <p className="text-xs text-orange-300 leading-relaxed">
                            🔁 <strong>If no transition matches</strong> and agent stays on this node, rephrase the script naturally instead of repeating word-for-word
                            <br />
                            💡 <strong>Best for:</strong> Greetings, confirmations, or any script that might need retrying
                          </p>
                        </div>
                      </div>

                      {/* Rephrase Prompt Guidance - Only shows when checkbox is checked */}
                      {selectedNode.data.dynamic_rephrase && (
                        <div className="mt-3 pt-3 border-t border-orange-700/30">
                          <label className="text-xs text-gray-700 mb-1 block">Rephrase Instructions (Optional)</label>
                          <textarea
                            value={selectedNode.data.rephrase_prompt || ''}
                            onChange={(e) => updateNode(selectedNode.id, {
                              data: { ...selectedNode.data, rephrase_prompt: e.target.value }
                            })}
                            className="bg-white border border-orange-700/50 text-gray-900 text-xs w-full px-3 py-2 rounded"
                            rows={3}
                            placeholder="e.g., 'If user seems confused, ask more directly. Keep the same friendly tone but vary the wording.'"
                          />
                          <p className="text-xs text-gray-500 mt-1">
                            Guide how the AI rephrases this script when retrying
                          </p>
                        </div>
                      )}
                        </div>
                      )}
                    </div>
                  )}


                  {/* 1. Extract Variables (State) */}
                  <div className="bg-white border border-gray-200 rounded-lg p-4 shadow-sm">
                    <h4 className="text-gray-900 font-medium mb-2">📊 Extract Variables (Real-Time)</h4>
                    <p className="text-xs text-gray-500 mb-3">
                      Extract variables during this conversation - useful for capturing information before webhooks
                    </p>

                    {(selectedNode.data?.extract_variables || []).map((variable, index) => (
                      <div key={index} className="mb-4 p-4 bg-gray-50/50 rounded border border-gray-200">
                        <div className="flex items-start gap-2 mb-3">
                          <input
                            type="text"
                            value={extractionVariableName(variable)}
                            onChange={(e) => {
                              const newVars = [...(selectedNode.data?.extract_variables || [])];
                              newVars[index] = withCanonicalExtractionVariableName(
                                newVars[index],
                                e.target.value
                              );
                              updateNode(selectedNode.id, {
                                data: { ...selectedNode.data, extract_variables: newVars }
                              });
                            }}
                            className="bg-white border border-gray-200 text-gray-900 flex-1 px-3 py-2 rounded font-semibold"
                            placeholder="Variable name (e.g., appointment_date)"
                          />
                          <button
                            onClick={() => {
                              const newVars = (selectedNode.data?.extract_variables || []).filter((_, i) => i !== index);
                              updateNode(selectedNode.id, {
                                data: { ...selectedNode.data, extract_variables: newVars }
                              });
                            }}
                            className="px-3 py-2 bg-red-600 hover:bg-red-700 text-gray-900 rounded"
                          >
                            ✕
                          </button>
                        </div>

                        {/* Description */}
                        <div className="mb-3">
                          <label className="text-xs text-gray-500 mb-1 block">Description</label>
                          <textarea
                            value={variable.description || ''}
                            onChange={(e) => {
                              const newVars = [...(selectedNode.data?.extract_variables || [])];
                              newVars[index] = { ...newVars[index], description: e.target.value };
                              updateNode(selectedNode.id, {
                                data: { ...selectedNode.data, extract_variables: newVars }
                              });
                            }}
                            className="bg-white border border-gray-200 text-gray-900 text-xs w-full px-3 py-2 rounded"
                            rows={2}
                            placeholder="What to extract (e.g., 'The date mentioned by user for appointment')"
                          />
                        </div>

                        {/* Extraction Hint */}
                        <div className="mb-3">
                          <label className="text-xs text-gray-500 mb-1 block">Extraction Hint (Optional)</label>
                          <input
                            type="text"
                            value={variable.extraction_hint || ''}
                            onChange={(e) => {
                              const newVars = [...(selectedNode.data?.extract_variables || [])];
                              newVars[index] = { ...newVars[index], extraction_hint: e.target.value };
                              updateNode(selectedNode.id, {
                                data: { ...selectedNode.data, extract_variables: newVars }
                              });
                            }}
                            className="bg-white border border-gray-200 text-gray-900 text-xs w-full px-3 py-2 rounded"
                            placeholder="e.g., 'Look for phrases like tomorrow, next week, specific dates'"
                          />
                          <p className="text-xs text-gray-500 mt-1">Helps AI know what patterns to look for</p>
                        </div>

                        {/* Calculation Feature */}
                        <div className="mb-3">
                          <label className="text-xs text-gray-500 mb-1 block">Calculation Feature (Optional)</label>
                          <Select
                            value={variable.calculation_type || 'none'}
                            onValueChange={(value) => {
                              const newVars = [...(selectedNode.data?.extract_variables || [])];
                              if (value === 'none') {
                                delete newVars[index].calculation_type;
                                delete newVars[index].round_to_500;
                              } else {
                                newVars[index] = { ...newVars[index], calculation_type: value };
                              }
                              updateNode(selectedNode.id, {
                                data: { ...selectedNode.data, extract_variables: newVars }
                              });
                            }}
                          >
                            <SelectTrigger className="bg-white border-gray-200 text-purple-200 mt-1 mb-2 text-xs py-1 h-8">
                              <SelectValue placeholder="Standard LLM Extraction" />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="none">Standard LLM Extraction</SelectItem>
                              <SelectItem value="income_normalization">Income Normalization (Backend Math)</SelectItem>
                            </SelectContent>
                          </Select>
                          <p className="text-xs text-gray-500 mt-1">
                            Bypasses the LLM and calculates using fixed rules in the backend.
                          </p>
                        </div>

                        {variable.calculation_type && variable.calculation_type !== 'none' && (
                          <div className="mb-3 flex items-center gap-2">
                            <input
                              type="checkbox"
                              checked={variable.round_to_500 === true}
                              onChange={(e) => {
                                const newVars = [...(selectedNode.data?.extract_variables || [])];
                                newVars[index] = { ...newVars[index], round_to_500: e.target.checked };
                                updateNode(selectedNode.id, {
                                  data: { ...selectedNode.data, extract_variables: newVars }
                                });
                              }}
                              className="w-4 h-4 text-purple-600 bg-white border-gray-200 rounded"
                            />
                            <label className="text-xs text-gray-700">
                              <span className="font-semibold text-purple-400">Round to Nearest 500</span> - e.g., 6782 becomes 7000
                            </label>
                          </div>
                        )}

                        {/* Allow Update Checkbox */}
                        <div className="mb-3 flex items-center gap-2">
                          <input
                            type="checkbox"
                            checked={variable.allow_update === true}
                            onChange={(e) => {
                              const newVars = [...(selectedNode.data?.extract_variables || [])];
                              newVars[index] = { ...newVars[index], allow_update: e.target.checked };
                              updateNode(selectedNode.id, {
                                data: { ...selectedNode.data, extract_variables: newVars }
                              });
                            }}
                            className="w-4 h-4 text-blue-600 bg-white border-gray-200 rounded"
                          />
                          <label className="text-xs text-gray-700">
                            <span className="font-semibold text-blue-400">Allow Update</span> - Re-extract this variable each time node is visited
                          </label>
                        </div>

                        {/* Mandatory Checkbox */}
                        <div className="mb-3 flex items-center gap-2">
                          <input
                            type="checkbox"
                            checked={extractionVariableIsMandatory(variable)}
                            onChange={(e) => {
                              const newVars = [...(selectedNode.data?.extract_variables || [])];
                              newVars[index] = withCanonicalExtractionMandatory(
                                newVars[index],
                                e.target.checked
                              );
                              updateNode(selectedNode.id, {
                                data: { ...selectedNode.data, extract_variables: newVars }
                              });
                            }}
                            className="w-4 h-4 text-blue-600 bg-white border-gray-200 rounded"
                          />
                          <label className="text-xs text-gray-700">
                            <span className="font-semibold text-orange-400">Mandatory</span> - Must be collected before transitioning
                          </label>
                        </div>

                        {/* Prompt Message (only show if mandatory) */}
                        {extractionVariableIsMandatory(variable) && (
                          <div>
                            <label className="text-xs text-gray-500 mb-1 block">Prompt Message (if missing)</label>
                            <textarea
                              value={variable.prompt_message || ''}
                              onChange={(e) => {
                                const newVars = [...(selectedNode.data?.extract_variables || [])];
                                newVars[index] = { ...newVars[index], prompt_message: e.target.value };
                                updateNode(selectedNode.id, {
                                  data: { ...selectedNode.data, extract_variables: newVars }
                                });
                              }}
                              className="bg-white border border-orange-700/50 text-gray-900 text-xs w-full px-3 py-2 rounded"
                              rows={2}
                              placeholder="Ask the user to provide their preferred time for the appointment"
                            />
                            <p className="text-xs text-gray-500 mt-1">
                              AI will use this instruction to naturally ask for the missing information
                            </p>
                          </div>
                        )}
                      </div>
                    ))}

                    <button
                      onClick={() => {
                        const newVars = [...(selectedNode.data?.extract_variables || []), { name: '', description: '', extraction_hint: '' }];
                        updateNode(selectedNode.id, {
                          data: { ...selectedNode.data, extract_variables: newVars }
                        });
                      }}
                      className="w-full px-4 py-2 bg-green-600 hover:bg-green-700 text-gray-900 rounded text-sm"
                    >
                      + Add Variable to Extract
                    </button>
                  </div>

                  {/* --- Virevo Standard Node Structure (VSNS) --- */}

                  {/* 1. Active Goal */}
                  <div className="bg-white border border-gray-200 rounded-lg p-4 shadow-sm mt-4">
                    <h4 className="text-gray-900 font-medium mb-2">🎯 Active Goal</h4>
                    <p className="text-xs text-gray-500 mb-3">
                      Description of what the node must achieve.
                    </p>
                    <Textarea
                      value={selectedNode.data.goal || ''}
                      onChange={(e) => updateNode(selectedNode.id, {
                        data: { ...selectedNode.data, goal: e.target.value }
                      })}
                      className="bg-white border-gray-300 text-gray-900"
                      rows={2}
                      placeholder="e.g., 'Get the user to provide their appointment date and time preference'"
                    />
                  </div>

                  {/* 2. Opening Gambit */}
                  <div className={`bg-white border rounded-lg p-4 mt-4 shadow-sm ${selectedNode.data.mode === 'script' ? 'border-blue-300 ring-1 ring-blue-100' : 'border-gray-200'}`}>
                    <h4 className="text-gray-900 font-medium mb-2">🗣️ Opening Gambit</h4>
                    <p className="text-xs text-gray-500 mb-3">
                      The exact core words the agent should say when first entering.
                    </p>
                    {selectedNode.data.mode === 'script' && (
                      <div className="mb-3 p-2.5 bg-blue-50 rounded-lg border border-blue-200 flex items-start gap-2">
                        <span className="text-blue-400 text-sm mt-0.5">📝</span>
                        <p className="text-xs text-blue-700 leading-relaxed">
                          <strong>Script Mode Active</strong> — The agent will speak this text <em>verbatim</em>. This is the field that gets read in script mode.
                        </p>
                      </div>
                    )}
                    <Textarea
                      value={selectedNode.data.opening_gambit || ''}
                      onChange={(e) => updateNode(selectedNode.id, {
                        data: { ...selectedNode.data, opening_gambit: e.target.value }
                      })}
                      className="bg-white border-gray-300 text-gray-900"
                      rows={3}
                      placeholder="e.g., 'Okay, let\'s get you scheduled. What time works best for you?'"
                    />
                  </div>

                  {/* 3. Strict Guardrails */}
                  <div className="bg-white border border-gray-200 rounded-lg p-4 shadow-sm mt-4">
                    <h4 className="text-gray-900 font-medium mb-2">🛡️ Strict Guardrails</h4>
                    <p className="text-xs text-gray-500 mb-3">
                      Crucial rules and negative restraints.
                    </p>
                    <Textarea
                      value={selectedNode.data.rules || ''}
                      onChange={(e) => updateNode(selectedNode.id, {
                        data: { ...selectedNode.data, rules: e.target.value }
                      })}
                      className="bg-white border-gray-300 text-gray-900"
                      rows={3}
                      placeholder="e.g., '- Do not answer pricing questions yet.\n- Never say the word \'cheap\'.'"
                    />
                  </div>

                  {/* 4. Strategic Toolkit / Core Instructions */}
                  <div className="bg-white border border-gray-200 rounded-lg p-4 shadow-sm mt-4">
                    <div className="flex items-center justify-between mb-2">
                      <h4 className="text-gray-900 font-medium">⚔️ Strategic Toolkit</h4>
                      <Button
                        onClick={handleOptimizeClick}
                        size="sm"
                        className="bg-purple-600 hover:bg-purple-700 text-gray-900"
                      >
                        <Sparkles size={14} className="mr-1" />
                        Optimize with AI
                      </Button>
                    </div>
                    <p className="text-xs text-gray-500 mb-3">
                      Handlers for objections/questions and core logical instructions.
                    </p>
                    <Textarea
                      value={selectedNode.data.content || ''}
                      onChange={(e) => updateNode(selectedNode.id, {
                        data: { ...selectedNode.data, content: e.target.value }
                      })}
                      className="bg-white border-gray-300 text-gray-900"
                      rows={6}
                      placeholder="e.g., '- **Tactic: User objects to price**\n  - **AGENT SAYS**: I completely understand...'"
                    />
                  </div>

                  {/* 5. Per-Node Voice Settings (ElevenLabs) */}
                  {agent?.settings?.tts_provider === 'elevenlabs' && (
                    <div className="bg-white border border-gray-200 rounded-lg p-4 shadow-sm">
                      <h4 className="text-gray-900 font-medium mb-2">🎙️ Voice Settings Override</h4>
                      <p className="text-xs text-gray-500 mb-3">
                        Override agent's default voice settings for this node. Leave empty to use agent defaults.
                      </p>

                      {/* Stability */}
                      <div className="mb-4">
                        <div className="flex justify-between items-center mb-1">
                          <label className="text-xs text-gray-700">Stability</label>
                          <span className="text-xs text-pink-400">
                            {selectedNode.data?.voice_settings?.stability ?? 'default'}
                          </span>
                        </div>
                        <input
                          type="range"
                          min="0"
                          max="1"
                          step="0.05"
                          value={selectedNode.data?.voice_settings?.stability ?? 0.5}
                          onChange={(e) => updateNode(selectedNode.id, {
                            data: {
                              ...selectedNode.data,
                              voice_settings: {
                                ...(selectedNode.data.voice_settings || {}),
                                stability: parseFloat(e.target.value)
                              }
                            }
                          })}
                          className="custom-slider"
                        />
                        <p className="text-xs text-gray-500 mt-1">Lower = more expressive, Higher = more consistent</p>
                      </div>

                      {/* Similarity Boost */}
                      <div className="mb-4">
                        <div className="flex justify-between items-center mb-1">
                          <label className="text-xs text-gray-700">Similarity Boost</label>
                          <span className="text-xs text-pink-400">
                            {selectedNode.data?.voice_settings?.similarity_boost ?? 'default'}
                          </span>
                        </div>
                        <input
                          type="range"
                          min="0"
                          max="1"
                          step="0.05"
                          value={selectedNode.data?.voice_settings?.similarity_boost ?? 0.75}
                          onChange={(e) => updateNode(selectedNode.id, {
                            data: {
                              ...selectedNode.data,
                              voice_settings: {
                                ...(selectedNode.data.voice_settings || {}),
                                similarity_boost: parseFloat(e.target.value)
                              }
                            }
                          })}
                          className="custom-slider"
                        />
                        <p className="text-xs text-gray-500 mt-1">Higher = closer to original voice</p>
                      </div>

                      {/* Style */}
                      <div className="mb-4">
                        <div className="flex justify-between items-center mb-1">
                          <label className="text-xs text-gray-700">Style Exaggeration</label>
                          <span className="text-xs text-pink-400">
                            {selectedNode.data?.voice_settings?.style ?? 'default'}
                          </span>
                        </div>
                        <input
                          type="range"
                          min="0"
                          max="1"
                          step="0.05"
                          value={selectedNode.data?.voice_settings?.style ?? 0}
                          onChange={(e) => updateNode(selectedNode.id, {
                            data: {
                              ...selectedNode.data,
                              voice_settings: {
                                ...(selectedNode.data.voice_settings || {}),
                                style: parseFloat(e.target.value)
                              }
                            }
                          })}
                          className="custom-slider"
                        />
                        <p className="text-xs text-gray-500 mt-1">Higher = more dramatic, emotional delivery</p>
                      </div>

                      {/* Speed */}
                      <div className="mb-4">
                        <div className="flex justify-between items-center mb-1">
                          <label className="text-xs text-gray-700">Speed</label>
                          <span className="text-xs text-pink-400">
                            {selectedNode.data?.voice_settings?.speed ?? 'default'}x
                          </span>
                        </div>
                        <input
                          type="range"
                          min="0.5"
                          max="2"
                          step="0.05"
                          value={selectedNode.data?.voice_settings?.speed ?? 1.0}
                          onChange={(e) => updateNode(selectedNode.id, {
                            data: {
                              ...selectedNode.data,
                              voice_settings: {
                                ...(selectedNode.data.voice_settings || {}),
                                speed: parseFloat(e.target.value)
                              }
                            }
                          })}
                          className="custom-slider"
                        />
                        <p className="text-xs text-gray-500 mt-1">0.5x = slow, 1.0x = normal, 2.0x = fast</p>
                      </div>

                      {/* Reset Button */}
                      <button
                        onClick={() => updateNode(selectedNode.id, {
                          data: { ...selectedNode.data, voice_settings: undefined }
                        })}
                        className="text-xs text-pink-400 hover:text-pink-300 underline"
                      >
                        Reset to agent defaults
                      </button>
                    </div>
                  )}

                  {/* 6. Call Control (Rambler Interruption) Settings */}
                  <div className="bg-white border border-gray-200 rounded-lg p-4 shadow-sm">
                    <h4 className="text-gray-900 font-medium mb-2">🚦 Call Control (Rambler Interruption)</h4>
                    <p className="text-xs text-gray-500 mb-3">
                      Configure when the agent should interrupt a user who is speaking too long
                    </p>

                    {/* Enable/Disable Override */}
                    <div className="mb-4 flex items-center gap-2">
                      <input
                        type="checkbox"
                        checked={selectedNode.data?.interruption_settings?.enabled === true}
                        onChange={(e) => updateNode(selectedNode.id, {
                          data: {
                            ...selectedNode.data,
                            interruption_settings: {
                              enabled: e.target.checked,
                              word_count_threshold: selectedNode.data?.interruption_settings?.word_count_threshold ?? 50,
                              interruption_prompt: selectedNode.data?.interruption_settings?.interruption_prompt || ''
                            }
                          }
                        })}
                        className="w-4 h-4 text-red-600 bg-white border-gray-200 rounded focus:ring-red-500"
                      />
                      <label className="text-xs text-gray-700">
                        <span className="font-semibold text-red-400">Enable Interruption</span> on this node (uses global settings if not overridden)
                      </label>
                    </div>

                    {/* Word Count Threshold Slider */}
                    <div className="mb-4">
                      <div className="flex justify-between items-center mb-1">
                        <label className="text-xs text-gray-700">Word Count Threshold</label>
                        <span className="text-xs text-red-400">
                          {selectedNode.data?.interruption_settings?.word_count_threshold ?? 'default (50)'}
                        </span>
                      </div>
                      <input
                        type="range"
                        min="10"
                        max="100"
                        step="5"
                        value={selectedNode.data?.interruption_settings?.word_count_threshold ?? 50}
                        onChange={(e) => updateNode(selectedNode.id, {
                          data: {
                            ...selectedNode.data,
                            interruption_settings: {
                              enabled: selectedNode.data?.interruption_settings?.enabled === true,
                              word_count_threshold: parseInt(e.target.value),
                              interruption_prompt: selectedNode.data?.interruption_settings?.interruption_prompt || ''
                            }
                          }
                        })}
                        className="custom-slider"
                      />
                      <p className="text-xs text-gray-500 mt-1">Interrupt after this many words (lower = more aggressive)</p>
                    </div>

                    {/* Custom Interruption Prompt */}
                    <div className="mb-3">
                      <label className="text-xs text-gray-500 mb-1 block">Custom Interruption Prompt (Optional)</label>
                      <textarea
                        value={selectedNode.data?.interruption_settings?.interruption_prompt || ''}
                        onChange={(e) => updateNode(selectedNode.id, {
                          data: {
                            ...selectedNode.data,
                            interruption_settings: {
                              enabled: selectedNode.data?.interruption_settings?.enabled === true,
                              word_count_threshold: selectedNode.data?.interruption_settings?.word_count_threshold ?? 50,
                              interruption_prompt: e.target.value
                            }
                          }
                        })}
                        className="bg-white border border-red-700/50 text-gray-900 text-xs w-full px-3 py-2 rounded"
                        rows={2}
                        placeholder="e.g., 'Politely acknowledge what the user said and steer back to the appointment time'"
                      />
                      <p className="text-xs text-gray-500 mt-1">
                        Leave empty to use agent's global interruption prompt
                      </p>
                    </div>

                    {/* Reset Button */}
                    <button
                      onClick={() => updateNode(selectedNode.id, {
                        data: { ...selectedNode.data, interruption_settings: undefined }
                      })}
                      className="text-xs text-red-400 hover:text-red-300 underline"
                    >
                      Reset to agent defaults
                    </button>
                  </div>
                </div>
              )}

              {/* 7. Patient Wait (Pause Dead Air) */}
              {selectedNode.type === 'conversation' && (
                <div className="bg-white border border-gray-200 rounded-lg p-4 shadow-sm mt-4">
                  <h4 className="text-gray-900 font-medium mb-2">⏳ Patient Wait (Pause Dead Air)</h4>
                  <p className="text-xs text-gray-500 mb-3">
                    Pause the "Are you still there?" dead air monitor temporarily while the user completes a real-world task.
                  </p>

                  {/* Enable/Disable Toggle */}
                  <div className="mb-4 flex items-center gap-2">
                    <input
                      type="checkbox"
                      checked={selectedNode.data?.patient_wait?.enabled === true}
                      onChange={(e) => updateNode(selectedNode.id, {
                        data: {
                          ...selectedNode.data,
                          patient_wait: {
                            ...(selectedNode.data.patient_wait || {}),
                            enabled: e.target.checked,
                            duration: selectedNode.data.patient_wait?.duration || 30
                          }
                        }
                      })}
                      className="w-4 h-4 text-emerald-600 bg-white border-gray-200 rounded focus:ring-emerald-500"
                    />
                    <label className="text-xs text-gray-700">
                      <span className="font-semibold text-emerald-400">Enable Patient Wait</span> on this node
                    </label>
                  </div>

                  {/* Duration Slider (only show if enabled) */}
                  {selectedNode.data?.patient_wait?.enabled && (
                    <div className="mb-2">
                      <div className="flex justify-between items-center mb-1">
                        <label className="text-xs text-gray-700">Wait Duration (Seconds)</label>
                        <span className="text-xs text-emerald-400">
                          {selectedNode.data?.patient_wait?.duration || 30}s
                        </span>
                      </div>
                      <input
                        type="range"
                        min="10"
                        max="90"
                        step="5"
                        value={selectedNode.data?.patient_wait?.duration || 30}
                        onChange={(e) => updateNode(selectedNode.id, {
                          data: {
                            ...selectedNode.data,
                            patient_wait: {
                              ...(selectedNode.data.patient_wait || {}),
                              duration: parseInt(e.target.value)
                            }
                          }
                        })}
                        className="custom-slider"
                      />
                      <p className="text-xs text-gray-500 mt-1">How long to suspend timeouts before prompting the user again.</p>
                    </div>
                  )}
                </div>
              )}

              {/* Transitions - not for start, transfer, logic_split, or press_digit nodes */}
              {selectedNode.type !== 'start' &&
                selectedNode.type !== 'call_transfer' &&
                selectedNode.type !== 'agent_transfer' &&
                selectedNode.type !== 'logic_split' &&
                selectedNode.type !== 'press_digit' && (
                  <div>
                    {/* Auto-Transition Toggle */}
                    <div className="border border-gray-200 rounded-lg p-4 bg-white shadow-sm mb-4">
                      <div className="flex items-start gap-3">
                        <input
                          type="checkbox"
                          checked={!!selectedNode.data.auto_transition_to}
                          onChange={(e) => {
                            if (e.target.checked) {
                              // Enable auto-transition with first available node
                              const firstAvailableNode = nodes.find(n => n.id !== selectedNode.id);
                              const newData = { ...selectedNode.data };
                              delete newData.auto_transition_after_response;
                              newData.auto_transition_to = firstAvailableNode?.id || '';
                              updateNode(selectedNode.id, { data: newData });
                            } else {
                              // Disable auto-transition
                              const newData = { ...selectedNode.data };
                              delete newData.auto_transition_to;
                              updateNode(selectedNode.id, { data: newData });
                            }
                          }}
                          className="mt-1 w-4 h-4 text-green-600 bg-white border-gray-200 rounded focus:ring-green-500"
                        />
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-1">
                            <Zap className="w-4 h-4 text-green-400" />
                            <label className="text-gray-900 font-medium text-sm cursor-pointer" onClick={(e) => {
                              e.preventDefault();
                              if (selectedNode.data.auto_transition_to) {
                                const newData = { ...selectedNode.data };
                                delete newData.auto_transition_to;
                                updateNode(selectedNode.id, { data: newData });
                              } else {
                                const firstAvailableNode = nodes.find(n => n.id !== selectedNode.id);
                                const newData = { ...selectedNode.data };
                                delete newData.auto_transition_after_response;
                                newData.auto_transition_to = firstAvailableNode?.id || '';
                                updateNode(selectedNode.id, { data: newData });
                              }
                            }}>
                              Auto-Transition (Skip Evaluation)
                            </label>
                          </div>
                          <p className="text-xs text-green-300 leading-relaxed">
                            ⚡ <strong>~574ms faster</strong> - Automatically move to next node without LLM evaluation
                            <br />
                            🎯 <strong>Best for:</strong> Linear flows where next node is always the same
                            <br />
                            ⚠️ <strong>Note:</strong> Disables condition-based transitions below
                          </p>
                        </div>
                      </div>
                      {selectedNode.data.auto_transition_to && (
                        <div className="mt-3">
                          <Label className="text-gray-700 text-sm mb-2 block">Next Node (Auto)</Label>
                          <div className="flex items-center gap-2">
                            <Select
                              value={selectedNode.data.auto_transition_to}
                              onValueChange={(value) => updateNode(selectedNode.id, {
                                data: { ...selectedNode.data, auto_transition_to: value }
                              })}
                            >
                              <SelectTrigger className="bg-gray-50 border-gray-600 text-gray-900 text-sm flex-1">
                                <SelectValue placeholder="Select next node..." />
                              </SelectTrigger>
                              <SelectContent>
                                {nodes.filter(n => n.id !== selectedNode.id).map((node) => (
                                  <SelectItem key={node.id} value={node.id}>
                                    {node.label}
                                  </SelectItem>
                                ))}
                              </SelectContent>
                            </Select>

                            {/* Jump to selected node button */}
                            {selectedNode.data.auto_transition_to && (
                              <button
                                onClick={() => {
                                  const targetNode = nodes.find(n => n.id === selectedNode.data.auto_transition_to);
                                  if (targetNode) {
                                    setSelectedNode(targetNode);
                                    // Scroll the node into view
                                    setTimeout(() => {
                                      const nodeElement = document.getElementById(`node-item-${targetNode.id}`);
                                      if (nodeElement) {
                                        nodeElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
                                      }
                                    }, 100);
                                  }
                                }}
                                className="p-2 bg-green-600 hover:bg-green-700 rounded text-gray-900 transition-colors flex-shrink-0"
                                title={`Jump to "${nodes.find(n => n.id === selectedNode.data.auto_transition_to)?.label}" node`}
                              >
                                <ArrowRight size={16} />
                              </button>
                            )}
                          </div>
                        </div>
                      )}
                    </div>

                    {/* Auto Transition After Response Toggle */}
                    <div className={`border border-gray-200 rounded-lg p-4 bg-white shadow-sm mb-4 ${selectedNode.data.auto_transition_to ? 'opacity-40 pointer-events-none' : ''}`}>
                      <div className="flex items-start gap-3">
                        <input
                          type="checkbox"
                          checked={!!selectedNode.data.auto_transition_after_response}
                          onChange={(e) => {
                            if (e.target.checked) {
                              // Enable auto-transition after response with first available node
                              const firstAvailableNode = nodes.find(n => n.id !== selectedNode.id);
                              // Clear existing transitions and set the auto_transition_after_response
                              const newData = { ...selectedNode.data };
                              delete newData.auto_transition_to;
                              newData.auto_transition_after_response = firstAvailableNode?.id || '';
                              newData.transitions = [];
                              updateNode(selectedNode.id, {
                                data: newData
                              });
                            } else {
                              // Disable auto-transition after response and restore default transition
                              const newData = { ...selectedNode.data };
                              delete newData.auto_transition_after_response;
                              newData.transitions = [{ id: Date.now().toString(), condition: '', nextNode: '' }];
                              updateNode(selectedNode.id, { data: newData });
                            }
                          }}
                          className="mt-1 w-4 h-4 text-blue-600 bg-white border-gray-200 rounded focus:ring-blue-500"
                          disabled={!!selectedNode.data.auto_transition_to}
                        />
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-1">
                            <ArrowRight className="w-4 h-4 text-blue-400" />
                            <label className="text-gray-900 font-medium text-sm cursor-pointer" onClick={(e) => {
                              e.preventDefault();
                              if (selectedNode.data.auto_transition_to) return; // Disabled if skip evaluation is active
                              if (selectedNode.data.auto_transition_after_response) {
                                const newData = { ...selectedNode.data };
                                delete newData.auto_transition_after_response;
                                newData.transitions = [{ id: Date.now().toString(), condition: '', nextNode: '' }];
                                updateNode(selectedNode.id, { data: newData });
                              } else {
                                const firstAvailableNode = nodes.find(n => n.id !== selectedNode.id);
                                const newData = { ...selectedNode.data };
                                delete newData.auto_transition_to;
                                newData.auto_transition_after_response = firstAvailableNode?.id || '';
                                newData.transitions = [];
                                updateNode(selectedNode.id, { data: newData });
                              }
                            }}>
                              Auto Transition After Response
                            </label>
                          </div>
                          <p className="text-xs text-blue-700 leading-relaxed">
                            🎤 <strong>Wait for user</strong> - Agent speaks, waits for user response, then auto-transitions
                            <br />
                            📝 <strong>Captures response</strong> - User's message is added to conversation context
                            <br />
                            🎯 <strong>Best for:</strong> When you don't care what user says, just need them to respond
                          </p>
                        </div>
                      </div>
                      {selectedNode.data.auto_transition_after_response && (
                        <div className="mt-3">
                          <Label className="text-gray-700 text-sm mb-2 block">Transition To (After User Responds)</Label>
                          <div className="flex items-center gap-2">
                            <Select
                              value={selectedNode.data.auto_transition_after_response}
                              onValueChange={(value) => updateNode(selectedNode.id, {
                                data: { ...selectedNode.data, auto_transition_after_response: value }
                              })}
                            >
                              <SelectTrigger className="bg-gray-50 border-gray-600 text-gray-900 text-sm flex-1">
                                <SelectValue placeholder="Select next node..." />
                              </SelectTrigger>
                              <SelectContent>
                                {nodes.filter(n => n.id !== selectedNode.id).map((node) => (
                                  <SelectItem key={node.id} value={node.id}>
                                    {node.label}
                                  </SelectItem>
                                ))}
                              </SelectContent>
                            </Select>

                            {/* Jump to selected node button */}
                            {selectedNode.data.auto_transition_after_response && (
                              <button
                                onClick={() => {
                                  const targetNode = nodes.find(n => n.id === selectedNode.data.auto_transition_after_response);
                                  if (targetNode) {
                                    setSelectedNode(targetNode);
                                    // Scroll the node into view
                                    setTimeout(() => {
                                      const nodeElement = document.getElementById(`node-item-${targetNode.id}`);
                                      if (nodeElement) {
                                        nodeElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
                                      }
                                    }, 100);
                                  }
                                }}
                                className="p-2 bg-blue-600 hover:bg-blue-700 rounded text-gray-900 transition-colors flex-shrink-0"
                                title={`Jump to "${nodes.find(n => n.id === selectedNode.data.auto_transition_after_response)?.label}" node`}
                              >
                                <ArrowRight size={16} />
                              </button>
                            )}
                          </div>
                          <p className="text-xs text-gray-500 mt-2">
                            Flow: Agent speaks → User says anything → Auto-transition to this node
                          </p>
                        </div>
                      )}
                    </div>

                    {/* Depth Gate Toggle */}
                    {selectedNode.type === 'conversation' && (
                      <div className={`border border-gray-200 rounded-lg p-4 bg-white shadow-sm mb-4 ${(selectedNode.data.auto_transition_to || selectedNode.data.auto_transition_after_response) ? 'opacity-40 pointer-events-none' : ''}`}>
                        <div className="flex items-start gap-3">
                          <input
                            type="checkbox"
                            checked={!!selectedNode.data.depth_gate_enabled}
                            onChange={(e) => updateNode(selectedNode.id, {
                              data: {
                                ...selectedNode.data,
                                depth_gate_enabled: e.target.checked,
                                depth_gate_threshold: selectedNode.data.depth_gate_threshold || 7
                              }
                            })}
                            className="mt-1 w-4 h-4 text-purple-600 bg-white border-gray-200 rounded focus:ring-purple-500"
                            disabled={!!selectedNode.data.auto_transition_to || !!selectedNode.data.auto_transition_after_response}
                          />
                          <div className="flex-1">
                            <div className="flex items-center gap-2 mb-1">
                              <Brain className="w-4 h-4 text-purple-500" />
                              <label
                                className="text-gray-900 font-medium text-sm cursor-pointer"
                                onClick={(e) => {
                                  e.preventDefault();
                                  if (selectedNode.data.auto_transition_to || selectedNode.data.auto_transition_after_response) return;
                                  updateNode(selectedNode.id, {
                                    data: {
                                      ...selectedNode.data,
                                      depth_gate_enabled: !selectedNode.data.depth_gate_enabled,
                                      depth_gate_threshold: selectedNode.data.depth_gate_threshold || 7
                                    }
                                  });
                                }}
                              >
                                Response Depth Gate
                              </label>
                            </div>
                            <p className="text-xs text-purple-700 leading-relaxed">
                              Scores nuanced answers before forward transitions can fire. Use this for pain, impact, desire, why-now, readiness, or other non-yes/no discovery moments.
                            </p>
                            {selectedNode.data.depth_gate_enabled && (
                              <div className="mt-3 grid grid-cols-[120px_1fr] gap-3 items-center">
                                <Label className="text-gray-700 text-xs">Minimum Score</Label>
                                <Input
                                  type="number"
                                  min="1"
                                  max="10"
                                  value={selectedNode.data.depth_gate_threshold || 7}
                                  onChange={(e) => {
                                    const nextValue = Math.max(1, Math.min(10, parseInt(e.target.value || '7', 10)));
                                    updateNode(selectedNode.id, {
                                      data: { ...selectedNode.data, depth_gate_threshold: nextValue }
                                    });
                                  }}
                                  className="bg-gray-50 border-gray-600 text-gray-900 text-sm h-9 w-24"
                                />
                                <div />
                                <p className="text-xs text-gray-500 leading-relaxed">
                                  Default is 7/10. Lower it for lighter qualifying questions; raise it when the agent must press for stronger emotional context.
                                </p>
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    )}

                    <div className="flex items-center justify-between mb-2">
                      <Label className="text-gray-700">
                        {selectedNode.data.auto_transition_to
                          ? 'Transitions (Disabled - Auto-Transition Active)'
                          : selectedNode.data.auto_transition_after_response
                            ? 'Transitions (Disabled - Auto After Response Active)'
                            : 'Transitions'}
                      </Label>
                      {!selectedNode.data.auto_transition_to && !selectedNode.data.auto_transition_after_response && (
                        <Button
                          onClick={addTransition}
                          size="sm"
                          className="bg-blue-600 hover:bg-blue-700"
                        >
                          <Plus size={14} className="mr-1" />
                          Add
                        </Button>
                      )}
                    </div>

                    {!selectedNode.data.auto_transition_to && !selectedNode.data.auto_transition_after_response && (
                      <div className="mb-3 p-3 bg-blue-50 border border-blue-200 rounded-lg shadow-sm">
                        <p className="text-xs text-blue-700 leading-relaxed">
                          <strong>⏸️ The [waiting] Feature:</strong> To make the agent pause while a user completes a task, tell the transition to look for the completed action (e.g., <em>"They indicate they have sent the text"</em>).
                        </p>
                        <p className="text-xs text-orange-700 mt-2 leading-relaxed">
                          ⚠️ <strong>Fast-Path Warning:</strong> Do not use words like <em>"okay", "sure", "yes", "agrees", "confirms"</em> in your condition text. These are hardcoded triggers that force an instant transition and skip the AI entirely!
                        </p>
                        <p className="text-xs text-purple-700 mt-2 leading-relaxed">
                          💡 <strong>How transitions work:</strong> When a transition fires, the AI generates its response using <strong>only the destination node's Goal</strong> — not its full content, rules, or strategic toolkit. Those only become visible on the <em>next</em> user message after arriving. To control what the AI says during the transition, use the <strong>Transition Goal/Instructions</strong> fields on the transition itself, or keep critical handling logic on the <em>source</em> node.
                        </p>
                      </div>
                    )}

                    <div className={`space-y-3 ${(selectedNode.data.auto_transition_to || selectedNode.data.auto_transition_after_response) ? 'opacity-40 pointer-events-none' : ''}`}>
                      {selectedNode.data.transitions?.map((transition) => (
                        <div key={transition.id} className="p-3 bg-white rounded-lg border border-gray-200">
                          <div className="space-y-2">
                            <div className="flex items-start gap-2">
                              <Textarea
                                value={transition.condition}
                                onChange={(e) => updateTransition(transition.id, 'condition', e.target.value)}
                                placeholder="Describe when to take this path (e.g., 'If user asks about pricing or wants to know costs' or 'If user needs technical support')"
                                className="bg-gray-50 border-gray-600 text-gray-900 text-sm flex-1"
                                rows={2}
                              />
                              {transition.condition && (
                                <button
                                  onClick={() => {
                                    setCurrentTransitionId(transition.id);
                                    setTransitionContent(transition.condition);
                                    setShowTransitionModal(true);
                                  }}
                                  className="p-2 bg-yellow-600 hover:bg-yellow-700 rounded text-gray-900 transition-colors flex-shrink-0"
                                  title="Optimize transition for faster evaluation"
                                >
                                  <Zap size={16} />
                                </button>
                              )}
                            </div>
                            <p className="text-xs text-gray-500 mt-1">
                              AI will analyze the conversation and decide if this condition is met
                            </p>
                            <div className="flex items-center gap-2">
                              <Select
                                value={canonicalTransitionTargetId(transition)}
                                onValueChange={(value) => updateTransition(transition.id, 'nextNode', value)}
                              >
                                <SelectTrigger className="bg-gray-50 border-gray-600 text-gray-900 text-sm flex-1">
                                  <SelectValue placeholder="Next node..." />
                                </SelectTrigger>
                                <SelectContent>
                                  <SelectItem value="END_CALL">
                                    <span className="flex items-center text-red-400 font-bold">
                                      🛑 End Call
                                    </span>
                                  </SelectItem>
                                  {nodes.filter(n => n.id !== selectedNode.id).map((node) => (
                                    <SelectItem key={node.id} value={node.id}>
                                      {node.label}
                                    </SelectItem>
                                  ))}
                                </SelectContent>
                              </Select>

                              {/* Jump to selected node button */}
                              {canonicalTransitionTargetId(transition) && (
                                <button
                                  onClick={() => {
                                    const targetNode = nodes.find(
                                      n => n.id === canonicalTransitionTargetId(transition)
                                    );
                                    if (targetNode) {
                                      setSelectedNode(targetNode);
                                      // Scroll the node into view in the sidebar
                                      setTimeout(() => {
                                        const nodeElement = document.getElementById(`node-item-${targetNode.id}`);
                                        if (nodeElement) {
                                          nodeElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
                                        }
                                      }, 100);
                                    }
                                  }}
                                  className="p-2 bg-blue-600 hover:bg-blue-700 rounded text-gray-900 transition-colors flex-shrink-0"
                                  title={`Jump to "${nodes.find(
                                    n => n.id === canonicalTransitionTargetId(transition)
                                  )?.label}" node`}
                                >
                                  <ArrowRight size={16} />
                                </button>
                              )}
                            </div>

                            {/* Transition Classification */}
                            <div className="mt-3 grid grid-cols-1 sm:grid-cols-2 gap-2">
                              <div>
                                <label className="text-xs text-gray-500 block mb-1">Transition type</label>
                                <Select
                                  value={transition.transition_type || ''}
                                  onValueChange={(value) => updateTransition(transition.id, 'transition_type', value)}
                                >
                                  <SelectTrigger className="bg-gray-50 border-gray-600 text-gray-900 text-xs">
                                    <SelectValue placeholder="Select type..." />
                                  </SelectTrigger>
                                  <SelectContent>
                                    <SelectItem value="valid_forward">Valid forward</SelectItem>
                                    <SelectItem value="script_continue">Script continue</SelectItem>
                                    <SelectItem value="local_question_answer">Local question answer</SelectItem>
                                    <SelectItem value="stay_current">Stay current</SelectItem>
                                    <SelectItem value="concern">Concern</SelectItem>
                                    <SelectItem value="compliance_exit">Compliance exit</SelectItem>
                                  </SelectContent>
                                </Select>
                              </div>
                              <div>
                                <label className="text-xs text-gray-500 block mb-1">Transition role</label>
                                <Select
                                  value={transition.transition_role || ''}
                                  onValueChange={(value) => updateTransition(transition.id, 'transition_role', value)}
                                >
                                  <SelectTrigger className="bg-gray-50 border-gray-600 text-gray-900 text-xs">
                                    <SelectValue placeholder="Select role..." />
                                  </SelectTrigger>
                                  <SelectContent>
                                    <SelectItem value="progress">Progress</SelectItem>
                                    <SelectItem value="progress_plus_process">Progress + process</SelectItem>
                                    <SelectItem value="stay_answer">Stay answer</SelectItem>
                                    <SelectItem value="nonprogress">Nonprogress</SelectItem>
                                    <SelectItem value="terminal">Terminal</SelectItem>
                                  </SelectContent>
                                </Select>
                              </div>
                            </div>

                            {/* Progress + Process Fluid Bridge */}
                            <div className="mt-3 flex items-start gap-2 p-2 bg-purple-900/10 border border-purple-700/20 rounded hover:bg-purple-900/20 transition-colors">
                              <input
                                type="checkbox"
                                checked={
                                  transition.fluid_transition_mode === 'progress_plus_process' ||
                                  transition.mixed_message_contract === 'progress_plus_process_fluid_transition'
                                }
                                onChange={(e) => {
                                  if (e.target.checked) {
                                    updateTransitionFields(transition.id, {
                                      fluid_transition_mode: 'progress_plus_process',
                                      mixed_message_contract: 'progress_plus_process_fluid_transition',
                                      transition_role: 'progress_plus_process',
                                      disable_bridging: false,
                                      fast_path_enabled: false
                                    });
                                  } else {
                                    updateTransitionFields(transition.id, {
                                      fluid_transition_mode: transition.fluid_transition_mode === 'progress_plus_process' ? '' : transition.fluid_transition_mode,
                                      mixed_message_contract: transition.mixed_message_contract === 'progress_plus_process_fluid_transition' ? '' : transition.mixed_message_contract,
                                      transition_role: transition.transition_role === 'progress_plus_process' ? '' : transition.transition_role
                                    });
                                  }
                                }}
                                className="mt-0.5 w-3.5 h-3.5 text-purple-600 bg-white border-gray-300 rounded focus:ring-purple-500 cursor-pointer"
                              />
                              <div>
                                <label
                                  className="text-xs text-purple-700 font-medium cursor-pointer"
                                  onClick={() => {
                                    const enabled = (
                                      transition.fluid_transition_mode === 'progress_plus_process' ||
                                      transition.mixed_message_contract === 'progress_plus_process_fluid_transition'
                                    );
                                    updateTransitionFields(transition.id, enabled ? {
                                      fluid_transition_mode: transition.fluid_transition_mode === 'progress_plus_process' ? '' : transition.fluid_transition_mode,
                                      mixed_message_contract: transition.mixed_message_contract === 'progress_plus_process_fluid_transition' ? '' : transition.mixed_message_contract,
                                      transition_role: transition.transition_role === 'progress_plus_process' ? '' : transition.transition_role
                                    } : {
                                      fluid_transition_mode: 'progress_plus_process',
                                      mixed_message_contract: 'progress_plus_process_fluid_transition',
                                      transition_role: 'progress_plus_process',
                                      disable_bridging: false,
                                      fast_path_enabled: false
                                    });
                                  }}
                                >
                                  Progress + process fluid bridge
                                </label>
                                <p className="text-[10px] text-gray-500 mt-0.5 leading-tight">
                                  Use when the caller satisfies this node and adds a setup/process question; pair it with a caller-facing transition response override below.
                                </p>
                              </div>
                            </div>

                            {/* Disable Bridging Phrase Checkbox */}
                            <div className="mt-3 flex items-start gap-2 p-2 bg-yellow-900/10 border border-yellow-700/20 rounded hover:bg-yellow-900/20 transition-colors">
                              <input
                                type="checkbox"
                                checked={!!transition.disable_bridging}
                                onChange={(e) => updateTransition(transition.id, 'disable_bridging', e.target.checked)}
                                className="mt-0.5 w-3.5 h-3.5 text-yellow-600 bg-white border-gray-300 rounded focus:ring-yellow-500 cursor-pointer"
                              />
                              <div>
                                <label 
                                  className="text-xs text-yellow-700 font-medium cursor-pointer" 
                                  onClick={() => updateTransition(transition.id, 'disable_bridging', !transition.disable_bridging)}
                                >
                                  Disable Bridging Phrase (Verbatim Transition)
                                </label>
                                <p className="text-[10px] text-gray-500 mt-0.5 leading-tight">
                                  Forces the agent to say the next node's opening gambit EXACTLY as written without summarizing or generating conversational filler first.
                                </p>
                              </div>
                            </div>

                            {/* Fast-Path Enabled Checkbox */}
                            <div className="mt-3 flex items-start gap-2 p-2 bg-green-900/10 border border-green-700/20 rounded hover:bg-green-900/20 transition-colors">
                              <input
                                type="checkbox"
                                checked={!!transition.fast_path_enabled}
                                onChange={(e) => updateTransition(transition.id, 'fast_path_enabled', e.target.checked)}
                                className="mt-0.5 w-3.5 h-3.5 text-green-600 bg-white border-gray-300 rounded focus:ring-green-500 cursor-pointer"
                              />
                              <div>
                                <label 
                                  className="text-xs text-green-700 font-medium cursor-pointer" 
                                  onClick={() => updateTransition(transition.id, 'fast_path_enabled', !transition.fast_path_enabled)}
                                >
                                  Enable 0ms Fast-Path for simple affirmations
                                </label>
                                <p className="text-[10px] text-gray-500 mt-0.5 leading-tight">
                                  If enabled, when the user replies with a simple one-word confirmation (like 'Yes' or 'Sure'), the system will instantly route to this transition without asking the LLM. Leave unchecked if the transition requires specific information (like an email address) that a simple 'Yes' wouldn't satisfy.
                                </p>
                              </div>
                            </div>

                            {/* Transition Goal & Instructions (Override) */}
                            <div className="mt-3 p-3 bg-purple-900/20 rounded border border-purple-700/30">
                              <label className="text-xs text-purple-300 mb-2 block font-medium">
                                ✏️ Transition Response Override <span className="text-gray-500">(Optional)</span>
                              </label>
                              <p className="text-xs text-gray-500 mb-2">
                                Control what the AI says when this transition fires. If empty, the AI uses the destination node's Goal.
                              </p>
                              <div className="space-y-2">
                                <div>
                                  <label className="text-xs text-gray-500 block mb-1">Goal</label>
                                  <Textarea
                                    value={transition.goal || ''}
                                    onChange={(e) => updateTransition(transition.id, 'goal', e.target.value)}
                                    placeholder="e.g., Answer their question from the KB first, then bridge to the value proposition"
                                    className="bg-gray-50 border-gray-600 text-gray-900 text-xs w-full"
                                    rows={2}
                                  />
                                </div>
                                <div>
                                  <label className="text-xs text-gray-500 block mb-1">Response Override <span className="text-gray-600">(exact script or bridging text)</span></label>
                                  <Textarea
                                    value={
                                      transition.transition_response_override ??
                                      transition.custom_transition_response ??
                                      transition.response_override ??
                                      transition.instructions ??
                                      ''
                                    }
                                    onChange={(e) => updateTransition(transition.id, 'transition_response_override', e.target.value)}
                                    placeholder="e.g., That's a great question — let me look into that for you."
                                    className="bg-gray-50 border-gray-600 text-gray-900 text-xs w-full"
                                    rows={2}
                                  />
                                </div>
                              </div>
                            </div>

                            {/* Variable Checks - Optional */}
                            <div className="mt-3 p-3 bg-gray-50/50 rounded border border-gray-200">
                              <label className="text-xs text-gray-500 mb-2 block font-medium">
                                🔍 Variable Checks (Optional)
                              </label>
                              <p className="text-xs text-gray-500 mb-2">
                                Require specific variables to have values before this transition can be taken
                              </p>

                              {/* Collect all variables from all nodes in the flow */}
                              {(() => {
                                const allVariables = new Set();
                                nodes.forEach(node => {
                                  const extractVars = node.data?.extract_variables || [];
                                  extractVars.forEach(v => {
                                    const variableName = extractionVariableName(v);
                                    if (variableName) allVariables.add(variableName);
                                  });
                                });

                                if (allVariables.size === 0) {
                                  return (
                                    <p className="text-xs text-gray-500 italic">
                                      No variables defined in any nodes yet
                                    </p>
                                  );
                                }

                                const checkVariables = transition.check_variables || [];

                                return (
                                  <div className="space-y-2">
                                    {Array.from(allVariables).map((varName) => (
                                      <label key={varName} className="flex items-center gap-2 cursor-pointer hover:bg-gray-200/30 p-1 rounded">
                                        <input
                                          type="checkbox"
                                          checked={checkVariables.includes(varName)}
                                          onChange={(e) => {
                                            let newCheckVars = [...(checkVariables || [])];
                                            if (e.target.checked) {
                                              newCheckVars.push(varName);
                                            } else {
                                              newCheckVars = newCheckVars.filter(v => v !== varName);
                                            }
                                            updateTransition(transition.id, 'check_variables', newCheckVars);
                                            updateTransition(transition.id, 'required_variables', newCheckVars);
                                          }}
                                          className="w-3 h-3 text-blue-600 bg-white border-gray-600 rounded"
                                        />
                                        <span className="text-xs text-gray-700">
                                          {varName}
                                        </span>
                                      </label>
                                    ))}
                                  </div>
                                );
                              })()}
                            </div>

                            {selectedNode.data.transitions.length > 1 && (
                              <Button
                                onClick={() => deleteTransition(transition.id)}
                                size="sm"
                                variant="ghost"
                                className="w-full text-red-400 hover:text-red-300 mt-2"
                              >
                                <Trash2 size={14} className="mr-2" />
                                Remove
                              </Button>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>

                    {/* Failed Transition Instructions (T:-1) */}
                    {!selectedNode.data.auto_transition_to && !selectedNode.data.auto_transition_after_response && (
                      <div className="mt-4 pt-4 border-t border-gray-200">
                        <Label className="text-gray-700">Failed Transition Instructions [T:-1]</Label>
                        <p className="text-xs text-gray-500 mb-2 mt-1">
                          Instructions for the AI if the user says something that does not match any transition above. (Tip: Reference ACTIVE GOAL and the AI will use the goal defined above).
                        </p>
                        <Textarea
                          value={selectedNode.data.failed_transition_instructions || 'Stay on current node and fulfill ACTIVE GOAL'}
                          onChange={(e) => updateNode(selectedNode.id, {
                            data: { ...selectedNode.data, failed_transition_instructions: e.target.value }
                          })}
                          className="bg-gray-50 border-gray-600 text-gray-900 text-sm w-full"
                          rows={3}
                          placeholder="Stay on current node and fulfill ACTIVE GOAL"
                        />
                      </div>
                    )}

                    <div className="mt-4 pt-4 border-t border-gray-200">
                      <Label className="text-gray-700">Render Contracts JSON</Label>
                      <Textarea
                        value={JSON.stringify(selectedNode.data?.render_contracts || [], null, 2)}
                        onChange={(e) => {
                          try {
                            updateNode(selectedNode.id, {
                              data: { ...selectedNode.data, render_contracts: JSON.parse(e.target.value || '[]') }
                            });
                          } catch {
                            updateNode(selectedNode.id, {
                              data: { ...selectedNode.data, render_contracts_draft: e.target.value }
                            });
                          }
                        }}
                        className="bg-gray-50 border-gray-600 text-gray-900 text-sm w-full"
                        rows={3}
                        placeholder='[]'
                      />
                    </div>

                    {/* Analyze All Transitions Button */}
                    {!selectedNode.data.auto_transition_to && !selectedNode.data.auto_transition_after_response && selectedNode.data.transitions && selectedNode.data.transitions.length > 0 && (
                      <div className="mt-4 pt-4 border-t border-gray-200">
                        <button
                          onClick={() => setShowAnalysisModal(true)}
                          className="w-full bg-purple-600 hover:bg-purple-700 text-gray-900 py-2.5 rounded-lg font-medium transition-colors flex items-center justify-center gap-2"
                        >
                          <Brain size={18} />
                          Analyze All Transitions (Detect Confusion & Overlap)
                        </button>
                        <p className="text-xs text-gray-500 mt-2 text-center">
                          See how the LLM interprets your transitions and identify potential issues
                        </p>
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}
      </div>

      {/* Optimize Node Modal */}
      <OptimizeNodeModal
        isOpen={showOptimizeModal}
        onClose={() => setShowOptimizeModal(false)}
        agentId={id}
        originalContent={optimizeContent}
        onApply={handleApplyOptimized}
      />

      <OptimizeTransitionModal
        isOpen={showTransitionModal}
        onClose={() => setShowTransitionModal(false)}
        agentId={id}
        originalCondition={transitionContent}
        onApply={(optimized) => {
          if (currentTransitionId) {
            updateTransition(currentTransitionId, 'condition', optimized);
          }
        }}
      />

      <TransitionAnalysisModal
        isOpen={showAnalysisModal}
        onClose={() => setShowAnalysisModal(false)}
        agentId={id}
        transitions={selectedNode?.data?.transitions || []}
        onApplyFix={(newTransitions) => {
          if (selectedNode) {
            updateNode(selectedNode.id, {
              data: { ...selectedNode.data, transitions: newTransitions }
            });
            toast({
              title: "Transitions Fixed",
              description: "The AI has automatically rewritten the transitions to resolve overlap/confusion.",
              // variant: "success" // using default since success might not exist
            });
          }
        }}
        onAnalysisComplete={(result) => {
          // Could show a toast with overall assessment
          if (result.confusion_warnings && result.confusion_warnings.length > 0) {
            toast({
              title: "Potential Issues Found",
              description: `${result.confusion_warnings.length} confusion/overlap warning(s) detected`,
              variant: "warning"
            });
          } else {
            toast({
              title: "Analysis Complete",
              description: "No confusion or overlap detected",
            });
          }
        }}
      />

      {/* Node Sequence Tester Modal */}
      <NodeSequenceTester
        agentId={id}
        isOpen={showNodeTester}
        onClose={() => setShowNodeTester(false)}
      />

      {/* AI Node Fixer Modal */}
      <AINodeFixer
        agentId={id}
        isOpen={showNodeFixer}
        onClose={() => setShowNodeFixer(false)}
        onNodesFixed={(result) => {
          toast({
            title: "Nodes Fixed",
            description: result.message,
          });
          fetchAgentFlow(); // Reload the flow to show fixed nodes
        }}
      />
    </div>
  );
};

export default FlowBuilder;
